import { getApiUrl, getApiUrlWithParams } from '../config/api';
import { REPORTS_API_USE_MOCK_DATA } from '../config/reportsMock';
import * as reportsApiMocks from '../mocks/reportsApiMocks';
import { hrDashboardMockData } from '../mocks/hrDashboardMock';
import { message } from 'antd';
import { applyValidationErrorsToForm } from './formHelpers';
import { processApiResponseWithPermissions } from './apiPermissionBridge';
import { devLog, devError } from './devLog';
import { padJalaliFilterPart } from './jalaliApiDate';
import {
  appendProductFormData,
  productPayloadHasFiles,
  serializeProductJsonPayload,
} from './productUtils';
import { formatJalaliApiDate } from './dateUtils';

// کلاس خطای سفارشی برای خطاهای 422
export class ValidationError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = 'ValidationError';
    this.details = details;
    this.status = 422;
  }
}

/** پیام پیش‌فرض عدم دسترسی (HTTP 403) */
export const FORBIDDEN_ACCESS_MESSAGE =
  'شما به این بخش دسترسی ندارید. برای دریافت مجوز، با مدیر سیستم هماهنگ کنید.';

/** خطای عدم دسترسی — HTTP 403 Forbidden */
export class ForbiddenError extends Error {
  constructor(message = FORBIDDEN_ACCESS_MESSAGE, details = {}) {
    super(message || FORBIDDEN_ACCESS_MESSAGE);
    this.name = 'ForbiddenError';
    this.details = details;
    this.status = 403;
  }
}

const PERMISSION_DENIED_MESSAGE_PATTERNS = [
  /^permission denied\.?$/i,
  /^forbidden\.?$/i,
  /^access denied\.?$/i,
  /^not authorized\.?$/i,
  /^unauthorized\.?$/i,
  /permission denied/i,
  /access denied/i,
  /عدم دسترسی/i,
  /دسترسی ندار/i,
  /مجوز.*ندار/i,
];

const isPermissionDeniedMessage = (msg) => {
  if (msg == null || String(msg).trim() === '') return false;
  const text = String(msg).trim();
  return PERMISSION_DENIED_MESSAGE_PATTERNS.some((pattern) => pattern.test(text));
};

const isGenericPermissionDeniedMessage = (msg) => {
  if (msg == null || String(msg).trim() === '') return true;
  return isPermissionDeniedMessage(msg);
};

/** پیام فارسی مناسب برای نمایش به کاربر در خطای 403 */
export const resolveForbiddenUserMessage = (backendMsg) => {
  if (backendMsg && !isGenericPermissionDeniedMessage(backendMsg)) {
    return String(backendMsg).trim();
  }
  return FORBIDDEN_ACCESS_MESSAGE;
};

const is403Response = (httpStatus, errorData) => {
  const backendMsg = errorData?.message || errorData?.error?.message;
  return (
    httpStatus === 403 ||
    errorData?.error?.code === 403 ||
    errorData?.error?.code === '403' ||
    isPermissionDeniedMessage(backendMsg)
  );
};

/** آیا بدنهٔ پاسخ API نشان‌دهندهٔ عدم دسترسی است؟ */
export const isPermissionDeniedPayload = (payload) =>
  Boolean(payload && typeof payload === 'object' && is403Response(null, payload));

/** نمایش پیام فارسی عدم دسترسی — هرگز آبجکت خام API را به message پاس ندهید */
export const showForbiddenAccessMessage = (backendMsg) => {
  message.warning({
    content: resolveForbiddenUserMessage(backendMsg),
    duration: 5,
  });
};

/** استخراج متن خطا برای نمایش — همیشه رشته برمی‌گرداند، نه آبجکت */
export const extractApiUserMessage = (error, defaultMessage = 'خطا در انجام عملیات') => {
  if (error == null) return defaultMessage;

  if (typeof error === 'string') {
    const text = error.trim();
    if (!text) return defaultMessage;
    return isPermissionDeniedMessage(text) ? resolveForbiddenUserMessage(text) : text;
  }

  if (typeof error !== 'object') {
    return defaultMessage;
  }

  if (isForbiddenError(error)) {
    const backendMsg =
      error?.message || error?.details?.message || error?.details?.error?.message;
    return resolveForbiddenUserMessage(backendMsg);
  }

  const backendMsg = error?.message || error?.error?.message || error?.details?.message;
  if (typeof backendMsg === 'string' && backendMsg.trim()) {
    return isPermissionDeniedMessage(backendMsg)
      ? resolveForbiddenUserMessage(backendMsg)
      : backendMsg.trim();
  }

  return defaultMessage;
};

/** استخراج پیام خطای قابل‌نمایش برای کاربر */
export const resolveApiErrorMessage = (errorData, httpStatus) => {
  const backendMsg = errorData?.message || errorData?.error?.message;
  if (is403Response(httpStatus, errorData)) {
    return resolveForbiddenUserMessage(backendMsg);
  }
  if (backendMsg) return backendMsg;
  return `HTTP error! status: ${httpStatus}`;
};

export const isForbiddenError = (error) => {
  if (!error) return false;
  if (
    error instanceof ForbiddenError ||
    error?.status === 403 ||
    error?.name === 'ForbiddenError'
  ) {
    return true;
  }
  if (isPermissionDeniedPayload(error) || isPermissionDeniedPayload(error?.details)) {
    return true;
  }
  const msg = error?.message || error?.error?.message;
  return isPermissionDeniedMessage(msg);
};

// تابع برای استخراج و فرمت کردن خطاهای 422
export const parse422Error = (errorData) => {
  if (!errorData || !errorData.error || !errorData.error.details) {
    return null;
  }

  const details = errorData.error.details;
  const errorMessages = [];

  // تبدیل details به آرایه‌ای از پیام‌ها
  Object.keys(details).forEach((field) => {
    const fieldErrors = Array.isArray(details[field]) ? details[field] : [details[field]];
    fieldErrors.forEach((errorMsg) => {
      // تبدیل نام فیلد به فارسی خوانا
      let fieldName = field
        .replace(/\./g, ' → ')
        .replace(/_/g, ' ');
      
      // تبدیل نام‌های فیلدهای رایج به فارسی
      const fieldTranslations = {
        'employee_detail': 'جزئیات کارمند',
        'employee detail': 'جزئیات کارمند',
        'employee_id': 'کد کارمند',
        'employee id': 'کد کارمند',
        'user_id': 'کارمند',
        'user id': 'کارمند',
        'employee_shift_id': 'شیفت',
        'employee shift id': 'شیفت',
        'shift_id': 'شیفت',
        'shift id': 'شیفت',
        'shift_date': 'تاریخ شیفت',
        'shift date': 'تاریخ شیفت',
        'leave_date': 'تاریخ مرخصی',
        'leave date': 'تاریخ مرخصی',
        'leave_type_id': 'نوع مرخصی',
        'leave type id': 'نوع مرخصی',
        'name': 'نام',
        'email': 'ایمیل',
        'mobile': 'موبایل',
        'reason': 'دلیل',
        'status': 'وضعیت',
        'duration': 'مدت زمان',
        'date': 'تاریخ',
        'remarks': 'یادداشت',
      };
      
      // اعمال ترجمه‌ها
      Object.keys(fieldTranslations).forEach((key) => {
        const regex = new RegExp(`\\b${key}\\b`, 'gi');
        fieldName = fieldName.replace(regex, fieldTranslations[key]);
      });
      
      errorMessages.push(`${fieldName}: ${errorMsg}`);
    });
  });

  return errorMessages.length > 0 ? errorMessages.join('\n') : null;
};

// تابع برای نمایش خطاهای 422 به کاربر
export const show422Error = (error, formOrOptions = null) => {
  const form = typeof formOrOptions?.setFields === 'function'
    ? formOrOptions
    : formOrOptions?.form ?? null;
  const formOptions = form
    ? (typeof formOrOptions === 'object' && !formOrOptions.setFields ? formOrOptions : {})
    : {};

  if (form && applyValidationErrorsToForm(form, error, formOptions)) {
    message.warning({
      content: 'لطفاً خطاهای فرم را برطرف کنید',
      duration: 4,
    });
    return;
  }

  let errorMessage = 'خطا در اعتبارسنجی داده‌ها';
  
  if (error instanceof ValidationError) {
    const parsedErrors = parse422Error({
      error: {
        details: error.details,
      },
    });
    if (parsedErrors) {
      errorMessage = parsedErrors;
    } else {
      errorMessage = error.message || errorMessage;
    }
  } else if (error.details) {
    // اگر error object مستقیم details دارد
    const parsedErrors = parse422Error({
      error: {
        details: error.details,
      },
    });
    if (parsedErrors) {
      errorMessage = parsedErrors;
    }
  } else if (error.error && error.error.details) {
    // اگر error در error.error.details است
    const parsedErrors = parse422Error(error);
    if (parsedErrors) {
      errorMessage = parsedErrors;
    }
  } else if (error?.message) {
    // ۴۲۲ بدون جزئیات فیلدها (مثلاً قوانین گردش کار) — پیام خود سرور نمایش داده شود
    errorMessage = error.message;
  }

  message.error({
    content: errorMessage,
    duration: 6,
    style: { whiteSpace: 'pre-line' },
  });
};

/** هدرهای درخواست — Content-Type صریح (مثلاً form-urlencoded) را بازنویسی نمی‌کند */
export const buildApiHeaders = (options = {}) => {
  const method = (options.method || 'GET').toUpperCase();
  const isFormData = options.body instanceof FormData;
  const hasBody = options.body != null && options.body !== '';
  const isReadWithoutBody = ['GET', 'HEAD'].includes(method) && !hasBody;

  const headers = {
    Accept: 'application/json',
    ...options.headers,
  };

  const hasContentType = Object.keys(headers).some(
    (key) => key.toLowerCase() === 'content-type'
  );

  // پیش‌فرض JSON فقط وقتی Content-Type از caller نیامده باشد
  if (!isReadWithoutBody && !isFormData && !hasContentType) {
    headers['Content-Type'] = 'application/json';
  }

  const token = localStorage.getItem('authToken');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

// تابع helper برای درخواست‌های API
export const apiRequest = async (url, options = {}) => {
  const token = localStorage.getItem('authToken');
  const isFormData = options.body instanceof FormData;
  const headers = buildApiHeaders(options);

  let debugBody;
  if (isFormData) {
    debugBody = '[FormData]';
  } else if (options.body && typeof options.body === 'string') {
    try {
      debugBody = JSON.parse(options.body);
    } catch {
      debugBody = options.body;
    }
  } else {
    debugBody = options.body;
  }

  devLog('🔵 API Request:', {
    url,
    method: options.method || 'GET',
    headers,
    hasToken: !!token,
    body: debugBody,
  });

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (typeof options.onResponse === 'function') {
      try {
        options.onResponse(response);
      } catch (callbackError) {
        console.warn('apiRequest onResponse callback failed:', callbackError);
      }
    }

    devLog('🟢 API Response:', {
      url,
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      devError('🔴 API Error:', {
        url,
        status: response.status,
        errorData,
      });
      devError('🔴 Full Error Response:', JSON.stringify(errorData, null, 2));
      
      // اگر خطای 401 است: خروج خودکار فقط وقتی توکن واقعاً ارسال شده (نه race قبل از لاگین)
      if (response.status === 401 || errorData?.error?.code === 401) {
        const hadAuthHeader = Boolean(
          headers.Authorization || headers.authorization,
        );
        if (!options.skipAuthRedirect && hadAuthHeader) {
          try {
            // جلوگیری از ریدایرکت‌های تکراری
            if (!window.__AUTH_REDIRECTING__) {
              window.__AUTH_REDIRECTING__ = true;
              localStorage.removeItem('authToken');
              localStorage.removeItem('userData');
              // ماژول action هم اگر لاگین داشته باشد پاک شود
              localStorage.removeItem('actionToken');
              localStorage.removeItem('actionUserData');
              // اگر همین الان داخل لاگین نیستیم، برو
              if (window.location.pathname !== '/login') {
                window.location.replace('/login');
              }
            }
          } catch (e) {
            // no-op
          }
        }
        throw new Error(errorData?.message || errorData?.error?.message || 'Not authenticated');
      }

      if (is403Response(response.status, errorData)) {
        throw new ForbiddenError(resolveApiErrorMessage(errorData, response.status), errorData);
      }
      
      // اگر خطای 422 است، ValidationError throw کن (فرمت CRM یا Laravel errors)
      if (response.status === 422) {
        const details = errorData.error?.details || errorData.errors;
        if (details && typeof details === 'object') {
          throw new ValidationError(
            errorData.message || errorData.error?.message || 'خطا در اعتبارسنجی داده‌ها',
            details,
          );
        }
      }

      // خطای ۴۰۹ (Conflict) — مثلاً هدف تکراری؛ بدنه کامل برای UI بازنویسی نگه داشته می‌شود
      if (response.status === 409) {
        const conflictError = new Error(
          resolveApiErrorMessage(errorData, response.status) || 'تداخل در ثبت داده',
        );
        conflictError.status = 409;
        conflictError.data = errorData;
        conflictError.has_duplicate = Boolean(
          errorData?.has_duplicate || errorData?.duplicate_info?.has_duplicate,
        );
        throw conflictError;
      }

      const genericError = new Error(resolveApiErrorMessage(errorData, response.status));
      genericError.status = response.status;
      genericError.data = errorData;
      throw genericError;
    }

    const payload = await response.json();

    if (is403Response(null, payload)) {
      throw new ForbiddenError(resolveApiErrorMessage(payload, 403), payload);
    }

    return processApiResponseWithPermissions(url, payload);
  } catch (error) {
    devError('🔴 API Request Failed:', {
      url,
      error: error.message,
      errorObject: error,
    });
    throw error;
  }
};

/**
 * دانلود فایل خروجی (اکسل/CSV) با هدر Authorization — برای روت‌های GET .../export
 */
export async function downloadExportFile(url, { filenameFallback = 'export.xlsx' } = {}) {
  const token = localStorage.getItem('authToken');
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv, application/octet-stream, */*',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    let msg = `خطا در خروجی (${response.status})`;
    let errorData = {};
    try {
      errorData = await response.json();
      msg = resolveApiErrorMessage(errorData, response.status);
    } catch {
      try {
        const t = await response.text();
        if (t) msg = t.slice(0, 200);
      } catch {
        /* noop */
      }
    }
    if (is403Response(response.status, errorData)) {
      throw new ForbiddenError(msg, errorData);
    }
    throw new Error(msg);
  }
  const blob = await response.blob();
  let filename = filenameFallback;
  const cd = response.headers.get('Content-Disposition');
  if (cd) {
    const utfMatch = /filename\\*=(?:UTF-8'')?([^;]+)/i.exec(cd);
    const asciiMatch = /filename="?([^";\n]+)"?/i.exec(cd);
    const raw = utfMatch?.[1] || asciiMatch?.[1];
    if (raw) {
      try {
        filename = decodeURIComponent(raw.trim().replace(/^["']|["']$/g, ''));
      } catch {
        filename = raw.trim();
      }
    }
  }
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

/** دریافت فایل با Authorization (پیش‌نمایش و غیره) */
export async function fetchAuthenticatedBlob(url) {
  const token = localStorage.getItem('authToken');
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: '*/*',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    throw new Error(`خطا در دریافت فایل (${response.status})`);
  }
  return response.blob();
}

// تابع‌های CRUD برای دپارتمان‌ها
export const departmentsApi = {
  getAll: async (filters = {}) => {
    const params = {
      fields: 'id,team_name,team_members_count,parent_id,parent{id,team_name}',
    };

    if (Object.keys(filters).length > 0) {
      const filterStrings = Object.keys(filters).map(key => {
        const value = filters[key];
        if (typeof value === 'string') {
          return `${key} eq "${value}"`;
        } else {
          return `${key} eq ${value}`;
        }
      });
      params.filters = filterStrings.join(' and ');
    }

    const url = getApiUrlWithParams('departments', params);
    console.log('📤 Departments API URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  getById: async (id) => {
    const params = {
      fields: 'id,team_name,team_members_count,parent_id,parent{id,team_name}',
    };
    const url = getApiUrlWithParams(`departments/${id}`, params);
    console.log('📤 Department By ID URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  create: async (data) => {
    const url = getApiUrl('departments');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id, data) => {
    const url = getApiUrl(`departments/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id) => {
    const url = getApiUrl(`departments/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

// تابع‌های CRUD برای سمت‌های شغلی (Designations)
export const designationsApi = {
  getAll: async (filters = {}) => {
    const params = {
      // مطابق departments: باید parent_id هم در fields باشد
      fields: 'id,name,members_count,parent_id,parent{id,name}',
    };

    if (Object.keys(filters).length > 0) {
      const filterStrings = Object.keys(filters).map((key) => {
        const value = filters[key];

        // فیلتر دقیق با کلید مستقیم
        if (typeof value === 'string') {
          return `${key} eq "${value}"`;
        }
        return `${key} eq ${value}`;
      });
      params.filters = filterStrings.join(' and ');
    }

    const url = getApiUrlWithParams('designations', params);
    console.log('📤 Designations API URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  getById: async (id) => {
    const params = {
      fields: 'id,name,parent{id,name}',
    };
    const url = getApiUrlWithParams(`designations/${id}`, params);
    console.log('📤 Designation By ID URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  create: async (data) => {
    const url = getApiUrl('designations');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id, data) => {
    const url = getApiUrl(`designations/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id) => {
    const url = getApiUrl(`designations/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  /** GET designations/export?parent_id= */
  exportList: async (filters = {}) => {
    const parentId =
      filters.parent_id != null && filters.parent_id !== '' ? filters.parent_id : 'all';
    const url = getApiUrlWithParams('designations/export', { parent_id: parentId });
    return downloadExportFile(url, { filenameFallback: 'designations.xlsx' });
  },
};

// تابع‌های CRUD برای کارمندان (Employees)
export const employeesApi = {
  getAll: async (filters = {}) => {
    const params = {
      // مطابق مستند: شامل جزئیات کارمند، مدیر و سمت
      fields: 'name,email,mobile,status,id,image,image_url,employeeDetail{employee_id},employeeDetail:reportingTo{id,name,image},employeeDetail:designation{id,name}',
    };

    if (Object.keys(filters).length > 0) {
      const filterStrings = Object.keys(filters).map((key) => {
        const value = filters[key];
        if (typeof value === 'string') {
          return `${key} eq "${value}"`;
        }
        return `${key} eq ${value}`;
      });
      params.filters = filterStrings.join(' and ');
    }

    const url = getApiUrlWithParams('employees', params);
    console.log('📤 Employees API URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  getById: async (id) => {
    const params = {
      fields: 'image,image_url,name,email,mobile,gender,status,national_id,father_name,insurance_code,locale,login,email_notifications,employeeDetail{date_of_birth,address,about_me,hourly_rate,employee_id,joining_date,probation_end_date,notice_period_start_date,notice_period_end_date,employment_type,internship_end_date,contract_end_date,marital_status,company_address_id,marriage_anniversary_date},employeeDetail:designation{id,name},employeeDetail:department{id,team_name},employeeDetail:reportingTo{id,name},reportingTeam:user{id,name,image_url},reportingTeam:designation{id,name},reportingTeam:department{id,team_name}',
    };
    const url = getApiUrlWithParams(`employees/${id}`, params);
    console.log('📤 Employee By ID URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  create: async (data) => {
    const url = getApiUrl('employees');
    if (data instanceof FormData) {
      return apiRequest(url, {
        method: 'POST',
        body: data,
      });
    }
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id, data) => {
    const url = getApiUrl(`employees/${id}`);
    // ویرایش کارمند همیشه POST + _method=PUT (با یا بدون فایل)
    if (data instanceof FormData) {
      if (!data.has('_method')) data.append('_method', 'PUT');
      return apiRequest(url, {
        method: 'POST',
        body: data,
      });
    }
    const body = { ...(data || {}), _method: 'PUT' };
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  delete: async (id) => {
    const url = getApiUrl(`employees/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  /** خلاصهٔ موارد باز کارمند برای واگذاری پیش از حذف */
  getHandoverSummary: async (id) => {
    const url = getApiUrl(`employees/${id}/handover`);
    return apiRequest(url, { method: 'GET' });
  },

  /** assignments: [{ category, user_ids: [] }] */
  handover: async (id, assignments) => {
    const url = getApiUrl(`employees/${id}/handover`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ assignments }),
    });
  },

  applyQuickAction: async (actionType, rowIds, status = null) => {
    const url = getApiUrl('employees/apply-quick-action');
    const payload = {
      action_type: actionType,
      row_ids: Array.isArray(rowIds) ? rowIds.join(',') : String(rowIds),
    };
    if (actionType === 'change-status' && status != null && status !== '') {
      payload.status = status;
    }
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * GET employees/export — پارامترها مطابق بک‌اند (همه با مقدار all وقتی خالی است)
   */
  exportList: async (filters = {}) => {
    const params = {
      gender: filters.gender && filters.gender !== '' ? filters.gender : 'all',
      status: filters.status && filters.status !== '' ? filters.status : 'all',
      designation_id:
        filters.designation_id != null && filters.designation_id !== ''
          ? filters.designation_id
          : 'all',
      department_id:
        filters.department_id != null && filters.department_id !== ''
          ? filters.department_id
          : 'all',
      role_id: filters.role_id != null && filters.role_id !== '' ? filters.role_id : 'all',
      employee_id:
        filters.employee_id != null && filters.employee_id !== '' ? filters.employee_id : 'all',
    };
    if (filters.search !== undefined && filters.search !== null && String(filters.search).trim() !== '') {
      params.search = String(filters.search).trim();
    }
    const url = getApiUrlWithParams('employees/export', params);
    return downloadExportFile(url, { filenameFallback: 'employees.xlsx' });
  },

  /** GET employees/import — متادیتا و فایل نمونه */
  getImportForm: async () => {
    const url = getApiUrl('employees/import');
    return apiRequest(url, { method: 'GET' });
  },

  /** POST employees/import — آپلود فایل */
  uploadImportFile: async (file, hasHeading) => {
    const url = getApiUrl('employees/import');
    const formData = new FormData();
    formData.append('import_file', file);
    formData.append('heading', hasHeading ? 'on' : 'off');
    return apiRequest(url, { method: 'POST', body: formData });
  },

  /** POST employees/import/process — شروع پردازش */
  processImport: async (payload) => {
    const url = getApiUrl('employees/import/process');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /** POST employees/create-invite-link */
  createInviteLink: async ({ allowEmail, emailDomain }) => {
    const url = getApiUrl('employees/create-invite-link');
    const body = {
      invitation_type: 'link',
      allow_email: allowEmail,
      email_domain: allowEmail === 'selected' ? emailDomain : null,
    };
    return apiRequest(url, { method: 'POST', body: JSON.stringify(body) });
  },

  /** POST employees/send-invite */
  sendInvite: async (emails, inviteMessage) => {
    const url = getApiUrl('employees/send-invite');
    const emailPayload = {};
    emails.forEach((addr, index) => {
      emailPayload[String(index)] = addr;
    });
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ email: emailPayload, message: inviteMessage || '' }),
    });
  },
};

// رول‌ها
export const rolesApi = {
  getAll: async () => {
    const url = getApiUrl('roles');
    const res = await apiRequest(url, { method: 'GET' });
    if (Array.isArray(res?.roles)) return res.roles;
    if (Array.isArray(res?.data?.roles)) return res.data.roles;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  },
};

// آدرس‌های شرکت
export const companyAddressesApi = {
  getAll: async () => {
    const url = getApiUrl('company-addresses');
    const res = await apiRequest(url, { method: 'GET' });
    return res?.data?.company_addresses ? res.data.company_addresses : res?.data || [];
  },
};

// تنظیمات عمومی شرکت
export const companySettingsApi = {
  get: async () => {
    const url = getApiUrl('company-settings');
    return apiRequest(url, { method: 'GET' });
  },
  update: async (id, payload) => {
    const url = getApiUrl(`company-settings/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
};

// نشانی کسب‌وکار (Settings)
export const businessAddressSettingsApi = {
  getAll: async () => {
    const url = getApiUrl('settings/business-address');
    return apiRequest(url, { method: 'GET' });
  },
  getCreateData: async () => {
    const url = getApiUrl('settings/business-address/create');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (payload) => {
    const url = getApiUrl('settings/business-address');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getEditData: async (id) => {
    const url = getApiUrl(`settings/business-address/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  update: async (id, payload) => {
    const url = getApiUrl(`settings/business-address/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`settings/business-address/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  setDefault: async (id) => {
    const url = getApiUrl('settings/business-address/set-default');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ id }),
    });
  },
};

/** تنظیمات پروفایل (تب‌ها: profile | emergency-contacts | documents) */
export const profileSettingsApi = {
  getTab: async (tab) => {
    const url = getApiUrlWithParams('settings/profile-settings', { tab });
    return apiRequest(url, { method: 'GET' });
  },
};

/** به‌روزرسانی پروفایل کاربر جاری */
export const userProfileApi = {
  update: async (userId, payload) => {
    const url = getApiUrl(`profile/${userId}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
};

/** تماس‌های اضطراری */
export const emergencyContactsApi = {
  create: async (payload) => {
    const url = getApiUrl('emergency-contacts');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getById: async (id) => {
    const url = getApiUrl(`emergency-contacts/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  update: async (id, payload) => {
    const url = getApiUrl(`emergency-contacts/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`emergency-contacts/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** مدارک پرسنلی (بارگذاری با FormData) */
export const employeeDocsApi = {
  create: async ({ name, file, user_id }) => {
    const url = getApiUrl('employee-docs');
    const fd = new FormData();
    fd.append('name', name);
    fd.append('file', file);
    fd.append('user_id', String(user_id));
    return apiRequest(url, { method: 'POST', body: fd });
  },
  getById: async (id) => {
    const url = getApiUrl(`employee-docs/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  update: async (id, payload) => {
    const url = getApiUrl(`employee-docs/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`employee-docs/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  download: async (id) => {
    const url = getApiUrl(`employee-docs/download/${id}`);
    return downloadExportFile(url, { filenameFallback: 'document' });
  },
};

/** تنظیمات ارز */
export const currencySettingsApi = {
  getAll: async () => {
    const url = getApiUrl('settings/currency-settings');
    return apiRequest(url, { method: 'GET' });
  },
  getById: async (id) => {
    const url = getApiUrl(`settings/currency-settings/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (payload) => {
    const url = getApiUrl('settings/currency-settings');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  update: async (id, payload) => {
    const url = getApiUrl(`settings/currency-settings/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`settings/currency-settings/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** بسته‌های سوپرادمین */
export const packagesApi = {
  getAll: async ({ page = 1, per_page = 10 } = {}) => {
    const url = getApiUrlWithParams('packages', { page, per_page });
    return apiRequest(url, { method: 'GET' });
  },
  getCreateMeta: async () => {
    const url = getApiUrl('packages/create');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (payload) => {
    const url = getApiUrl('packages');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getEditMeta: async (id) => {
    const url = getApiUrl(`packages/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  update: async (id, payload) => {
    const url = getApiUrl(`packages/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`packages/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** شرکت‌ها — سوپرادمین */
export const companiesApi = {
  list: async (params = {}) => {
    const url = getApiUrlWithParams('companies', params);
    return apiRequest(url, { method: 'GET' });
  },
  getCreateMeta: async () => {
    const url = getApiUrl('companies/create');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (formData) => {
    const url = getApiUrl('companies');
    return apiRequest(url, {
      method: 'POST',
      body: formData,
    });
  },
  show: async (id) => {
    const url = getApiUrl(`companies/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  getEditMeta: async (id) => {
    const url = getApiUrl(`companies/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  update: async (id, payload) => {
    const url = getApiUrl(`companies/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`companies/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  getEditPackageMeta: async (id) => {
    const url = getApiUrl(`companies/edit-package/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  updatePackage: async (id, payload) => {
    const url = getApiUrl(`companies/update-package/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
};

/** صورتحساب اشتراک — سوپرادمین */
export const superadminInvoicesApi = {
  list: async (params = {}) => {
    const url = getApiUrlWithParams('superadmin-invoices', params);
    return apiRequest(url, { method: 'GET' });
  },
};

/** بدنهٔ پاسخ FAQ — هم تخت ({ categories, faqs, faq }) و هم داخل `data` */
export const normalizeFaqApiBody = (res) => {
  if (!res || typeof res !== 'object') return {};
  const nested = res.data;
  if (nested != null && typeof nested === 'object') {
    const has =
      nested.categories != null ||
      nested.faqs != null ||
      nested.faq != null ||
      nested.types != null ||
      nested.superadmins != null;
    if (has) return nested;
  }
  return res;
};

/** تبدیل توضیحات ذخیره‌شده با تگ HTML به متن ساده (نمایش/ویرایش) */
export const faqDescriptionToPlainText = (value) => {
  if (value == null || value === '') return '';
  const str = String(value);
  if (!str.includes('<')) return str;
  if (typeof document !== 'undefined') {
    const el = document.createElement('div');
    el.innerHTML = str;
    const t = el.textContent || el.innerText;
    return (t || '').trim();
  }
  return str.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
};

/** سوالات متداول مدیر — سوپرادمین */
export const faqsApi = {
  index: async () => {
    const url = getApiUrl('faqs');
    return apiRequest(url, { method: 'GET' });
  },
  search: async (term) => {
    const url = getApiUrl(`faqs/search/${encodeURIComponent(String(term ?? ''))}`);
    return apiRequest(url, { method: 'GET' });
  },
  getCreateMeta: async () => {
    const url = getApiUrl('faqs/create');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (formData) => {
    const url = getApiUrl('faqs');
    return apiRequest(url, { method: 'POST', body: formData });
  },
  show: async (id) => {
    const url = getApiUrl(`faqs/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  getEditMeta: async (id) => {
    const url = getApiUrl(`faqs/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  update: async (id, formData) => {
    const url = getApiUrl(`faqs/${id}`);
    return apiRequest(url, { method: 'PUT', body: formData });
  },
  delete: async (id) => {
    const url = getApiUrl(`faqs/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  deleteFile: async (fileId) => {
    const url = getApiUrl(`faqs/file-destroy/${fileId}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** دسته‌بندی سوالات متداول — سوپرادمین */
export const faqCategoriesApi = {
  getCreateMeta: async () => {
    const url = getApiUrl('faq-category/create');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (payload) => {
    const url = getApiUrl('faq-category');
    return apiRequest(url, { method: 'POST', body: JSON.stringify(payload) });
  },
  update: async (id, payload) => {
    const url = getApiUrl(`faq-category/${id}`);
    return apiRequest(url, { method: 'PUT', body: JSON.stringify(payload) });
  },
  delete: async (id) => {
    const url = getApiUrl(`faq-category/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** کاربران سوپرادمین — سوپرادمین */
export const superadminUsersApi = {
  list: async (params = {}) => {
    const url = getApiUrlWithParams('superadmin', params);
    return apiRequest(url, { method: 'GET' });
  },
  getCreateMeta: async () => {
    const url = getApiUrl('superadmin/create');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (formData) => {
    const url = getApiUrl('superadmin');
    return apiRequest(url, { method: 'POST', body: formData });
  },
  getEditMeta: async (id) => {
    const url = getApiUrl(`superadmin/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  update: async (id, payload) => {
    const url = getApiUrl(`superadmin/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`superadmin/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  assignRole: async (payload) => {
    const url = getApiUrl('superadmin/assignRole');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

/** تیکت پشتیبانی (سوپرادمین) */
export const supportTicketsApi = {
  list: async (params = {}) => {
    const url = getApiUrlWithParams('support-tickets', params);
    return apiRequest(url, { method: 'GET' });
  },
  getCreateMeta: async () => {
    const url = getApiUrl('support-tickets/create');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (formData) => {
    const url = getApiUrl('support-tickets');
    return apiRequest(url, { method: 'POST', body: formData });
  },
  show: async (id) => {
    const url = getApiUrl(`support-tickets/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  /** پاسخ / تغییر وضعیت */
  update: async (id, payload) => {
    const url = getApiUrl(`support-tickets/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  /** agent_id, type_id, priority, status */
  updateOtherData: async (id, payload) => {
    const url = getApiUrl(`support-tickets/update-other-data/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`support-tickets/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  deleteFile: async (fileId) => {
    const url = getApiUrl(`support-tickets/delete-file/${fileId}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  deleteReply: async (replyId) => {
    const url = getApiUrl(`support-tickets/delete-reply/${replyId}`);
    return apiRequest(url, { method: 'GET' });
  },
  applyQuickAction: async (actionType, rowIds, status = null) => {
    const url = getApiUrl('support-tickets/apply-quick-action');
    const payload = {
      action_type: actionType,
      row_ids: Array.isArray(rowIds) ? rowIds.join(',') : rowIds,
    };
    if (actionType === 'change-status' && status) {
      payload.status = status;
    }
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  exportList: async (params = {}) => {
    const url = getApiUrlWithParams('support-tickets/export', params);
    return downloadExportFile(url, { filenameFallback: 'support-tickets.xlsx' });
  },
  downloadFile: async (fileId) => {
    const url = getApiUrl(`support-tickets/download-file/${fileId}`);
    return downloadExportFile(url, { filenameFallback: 'file' });
  },
};

/** انواع تیکت پشتیبانی — سوپرادمین */
export const supportTicketTypesApi = {
  getCreateMeta: async () => {
    const url = getApiUrl('support-ticket-types/create');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (payload) => {
    const url = getApiUrl('support-ticket-types');
    return apiRequest(url, { method: 'POST', body: JSON.stringify(payload) });
  },
  update: async (id, payload) => {
    const url = getApiUrl(`support-ticket-types/${id}`);
    return apiRequest(url, { method: 'PUT', body: JSON.stringify(payload) });
  },
  delete: async (id) => {
    const url = getApiUrl(`support-ticket-types/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** نقش‌ها و دسترسی‌ها */
export const rolePermissionsApi = {
  getIndex: async () => {
    const url = getApiUrl('settings/role-permissions');
    return apiRequest(url, { method: 'GET' });
  },
  getPermissions: async (roleId) => {
    const url = getApiUrl(`settings/role-permissions/permissions/${roleId}`);
    return apiRequest(url, { method: 'GET' });
  },
  getCreateMeta: async () => {
    const url = getApiUrl('settings/role-permissions/create');
    return apiRequest(url, { method: 'GET' });
  },
  createRole: async (payload) => {
    const url = getApiUrl('settings/role-permissions/store-role');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  deleteRole: async (roleId) => {
    const url = getApiUrl(`settings/role-permissions/delete-role/${roleId}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  resetPermissions: async (roleId) => {
    const url = getApiUrl(`settings/role-permissions/reset-permissions/${roleId}`);
    return apiRequest(url, { method: 'GET' });
  },
  getCustomPermissions: async (payload) => {
    const url = getApiUrl('settings/role-permissions/custom-permissions');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  updateRolePermission: async (payload) => {
    const url = getApiUrl('settings/role-permissions');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  updatePermission: async (scopeId, payload) => {
    if (payload?.role_id) {
      const url = getApiUrl('settings/role-permissions');
      return apiRequest(url, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    }
    const url = getApiUrl(`user-permissions/${scopeId}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
};

/** تنظیمات اپلیکیشن */
export const appSettingsApi = {
  getTab: async (tab = 'general') => {
    const url = getApiUrlWithParams('settings/app-settings', { tab });
    return apiRequest(url, { method: 'GET' });
  },
  update: async (companyId, payload) => {
    const url = getApiUrlWithParams(`settings/app-settings/${companyId}`, { page: 'app-settings' });
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
};

/** تنظیمات سوپرادمین — آپلود فایل (تب file-upload-setting) */
export const superadminAppSettingsApi = {
  getFileUpload: async () => {
    const url = getApiUrlWithParams('settings/app-settings', { tab: 'file-upload-setting' });
    return apiRequest(url, { method: 'GET' });
  },
  updateFileUpload: async (payload) => {
    const url = getApiUrl('settings/app-settings/update');
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify({
        page: 'file-upload-setting',
        ...payload,
      }),
    });
  },
};

/** تنظیمات پروفایل سوپرادمین */
export const superadminProfileSettingsApi = {
  get: async () => {
    const url = getApiUrl('settings/super-admin-profile');
    return apiRequest(url, { method: 'GET' });
  },
};

/** تنظیمات مالی سراسری سوپرادمین */
export const superadminGlobalInvoiceSettingsApi = {
  get: async () => {
    const url = getApiUrl('settings/global-invoice-settings');
    return apiRequest(url, { method: 'GET' });
  },
  update: async (payload) => {
    const url = getApiUrl('settings/global-invoice-settings/update');
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
};

/** تنظیمات زبان — سوپرادمین */
export const superadminLanguageSettingsApi = {
  list: async () => {
    const url = getApiUrl('settings/language-settings');
    return apiRequest(url, { method: 'GET' });
  },
  getCreateMeta: async () => {
    const url = getApiUrl('settings/language-settings/create');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (payload) => {
    const url = getApiUrl('settings/language-settings');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getEditMeta: async (id) => {
    const url = getApiUrl(`settings/language-settings/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  update: async (id, payload) => {
    const url = getApiUrl(`settings/language-settings/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`settings/language-settings/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  updateStatus: async (id, status) => {
    const url = getApiUrl(`settings/language-settings/update-status/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },
};

/** تنظیمات ارز سراسری — سوپرادمین */
export const superadminGlobalCurrencySettingsApi = {
  list: async () => {
    const url = getApiUrl('settings/global-currency-settings');
    return apiRequest(url, { method: 'GET' });
  },
  getEditMeta: async (id) => {
    const url = getApiUrl(`settings/global-currency-settings/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (payload) => {
    const url = getApiUrl('settings/global-currency-settings');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  update: async (id, payload) => {
    const url = getApiUrl(`settings/global-currency-settings/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`settings/global-currency-settings/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** خطاهای سیستم لاراول (Exceptions) — فقط سوپرادمین */
export const appExceptionsApi = {
  list: async (params = {}) => {
    const url = getApiUrlWithParams('app-exceptions', params);
    return apiRequest(url, { method: 'GET' });
  },
  get: async (id) => {
    const url = getApiUrl(`app-exceptions/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  stats: async () => {
    const url = getApiUrl('app-exceptions/stats');
    return apiRequest(url, { method: 'GET' });
  },
  delete: async (id) => {
    const url = getApiUrl(`app-exceptions/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  clearAll: async () => {
    const url = getApiUrl('app-exceptions/clear');
    return apiRequest(url, { method: 'DELETE' });
  },
  getSettings: async () => {
    const url = getApiUrl('app-exceptions/settings');
    return apiRequest(url, { method: 'GET' });
  },
  updateSettings: async (loggingEnabled) => {
    const url = getApiUrl('app-exceptions/settings');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ logging_enabled: loggingEnabled }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },
  test: async (payload = {}) => {
    const url = getApiUrl('app-exceptions/test');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },
};

/** تقویم من */
export const myCalendarApi = {
  getEvents: async ({ startDate, endDate, type = 'all' }) => {
    const url = getApiUrlWithParams('my-calendar', { startDate, endDate, type });
    return apiRequest(url, { method: 'GET' });
  },
  getDay: async ({ date, type = 'all' }) => {
    const url = getApiUrlWithParams('my-calendar/day', { date, type });
    return apiRequest(url, { method: 'GET' });
  },
};

/** داشبورد */
export const dashboardApi = {
  getPersonal: async () => {
    const url = getApiUrl('dashboard');
    return apiRequest(url, { method: 'GET' });
  },
  getAdvanced: async (tab = 'overview') => {
    const url = getApiUrlWithParams('dashboard-advanced', tab ? { tab } : {});
    return apiRequest(url, { method: 'GET' });
  },
};

/** داشبورد مدیریتی منابع انسانی (HR Management Dashboard) */
export const hrDashboardApi = {
  getSummary: async (params = {}) => {
    try {
      const url = getApiUrlWithParams('hr-dashboard', params);
      const res = await apiRequest(url, { method: 'GET' });
      if (res && res.data) return res;
      if (res && res.today) return { status: 'success', data: res };
      return { status: 'success', data: hrDashboardMockData };
    } catch (err) {
      devLog('hrDashboardApi.getSummary using mock fallback:', err);
      return { status: 'success', data: hrDashboardMockData };
    }
  },
  getToday: async (params = {}) => {
    try {
      const url = getApiUrlWithParams('hr-dashboard/today', params);
      return await apiRequest(url, { method: 'GET' });
    } catch (err) {
      devLog('hrDashboardApi.getToday using mock fallback:', err);
      return { status: 'success', data: hrDashboardMockData.today };
    }
  },
  getMonth: async (params = {}) => {
    try {
      const url = getApiUrlWithParams('hr-dashboard/month', params);
      return await apiRequest(url, { method: 'GET' });
    } catch (err) {
      devLog('hrDashboardApi.getMonth using mock fallback:', err);
      return { status: 'success', data: hrDashboardMockData.month };
    }
  },
  getAlerts: async (params = {}) => {
    try {
      const url = getApiUrlWithParams('hr-dashboard/alerts', params);
      return await apiRequest(url, { method: 'GET' });
    } catch (err) {
      devLog('hrDashboardApi.getAlerts using mock fallback:', err);
      return { status: 'success', data: hrDashboardMockData.alerts };
    }
  },
};

/** پیگیری‌ها — لیست کامل فعالیت‌ها */
export const followUpsApi = {
  getAll: async (filters = {}) => {
    const params = {
      status: filters.status != null && filters.status !== '' ? filters.status : 'all',
      follow_up_activity_id:
        filters.follow_up_activity_id != null && filters.follow_up_activity_id !== ''
          ? filters.follow_up_activity_id
          : 'all',
      assign_to: filters.assign_to != null && filters.assign_to !== '' ? filters.assign_to : 'all',
    };
    if (filters.startDate) params.startDate = formatJalaliApiDate(filters.startDate);
    if (filters.endDate) params.endDate = formatJalaliApiDate(filters.endDate);
    if (filters.search) params.search = filters.search;
    params.page = filters.page != null ? filters.page : 1;
    params.per_page = filters.per_page != null ? filters.per_page : 10;
    const url = getApiUrlWithParams('all-follow-ups', params);
    return apiRequest(url, { method: 'GET' });
  },
};

/** داشبورد سوپرادمین — پاسخ تخت یا داخل `data` */
export const normalizeSuperAdminDashboardBody = (res) => {
  if (!res || typeof res !== 'object') return {};
  const nested = res.data;
  if (nested != null && typeof nested === 'object' && nested.company_stats != null) return nested;
  if (res.company_stats != null) return res;
  return nested && typeof nested === 'object' ? nested : res;
};

export const superAdminDashboardApi = {
  get: async () => {
    const url = getApiUrl('super-admin-dashboard');
    return apiRequest(url, { method: 'GET' });
  },
};

const reportsMockExportNotice = () => {
  message.info('در حالت دادهٔ تستی، خروجی اکسل فعال نیست.');
};

// گزارشات
export const reportsApi = {
  getFinanceReport: async (filters = {}) => {
    if (REPORTS_API_USE_MOCK_DATA) return reportsApiMocks.getFinanceReport(filters);
    const url = getApiUrlWithParams('finance-report', {
      startDate: filters.startDate,
      endDate: filters.endDate,
      project_id: filters.project_id ?? 'all',
      client_id: filters.client_id ?? 'all',
      page: filters.page ?? 1,
      per_page: filters.per_page ?? 10,
    });
    return apiRequest(url, { method: 'GET' });
  },
  exportFinanceReport: async (filters = {}) => {
    if (REPORTS_API_USE_MOCK_DATA) {
      reportsMockExportNotice();
      return;
    }
    const url = getApiUrlWithParams('finance-report/export', {
      startDate: filters.startDate,
      endDate: filters.endDate,
      project_id: filters.project_id ?? 'all',
      client_id: filters.client_id ?? 'all',
    });
    return downloadExportFile(url, { filenameFallback: 'finance-report.xlsx' });
  },
  getIncomeExpenseReport: async (filters = {}) => {
    if (REPORTS_API_USE_MOCK_DATA) return reportsApiMocks.getIncomeExpenseReport(filters);
    const url = getApiUrlWithParams('income-expense-report', {
      startDate: filters.startDate,
      endDate: filters.endDate,
    });
    return apiRequest(url, { method: 'GET' });
  },
  getLeaveReport: async (filters = {}) => {
    if (REPORTS_API_USE_MOCK_DATA) return reportsApiMocks.getLeaveReport(filters);
    const url = getApiUrlWithParams('leave-report', {
      startDate: filters.startDate,
      endDate: filters.endDate,
      employee_id: filters.employee_id ?? 'all',
      page: filters.page ?? 1,
      per_page: filters.per_page ?? 10,
    });
    return apiRequest(url, { method: 'GET' });
  },
  getLeaveQuotaReport: async (filters = {}) => {
    if (REPORTS_API_USE_MOCK_DATA) return reportsApiMocks.getLeaveQuotaReport(filters);
    const monthStr = String(Number(filters.month) || 1).padStart(2, '0');
    const url = getApiUrlWithParams('leave-report/leave-quota', {
      month: monthStr,
      year: filters.year != null ? String(filters.year) : '',
      employee_id: filters.employee_id ?? 'all',
      page: filters.page ?? 1,
      per_page: filters.per_page ?? 10,
    });
    return apiRequest(url, { method: 'GET' });
  },
  getEmployeeLeaveQuotaDetail: async (employeeId, year, month) => {
    if (REPORTS_API_USE_MOCK_DATA) return reportsApiMocks.getEmployeeLeaveQuotaDetail(employeeId, year, month);
    const m = String(Number(month) || 1).padStart(2, '0');
    const url = getApiUrl(`leave-report/leave-quota/${employeeId}/${year}/${m}`);
    return apiRequest(url, { method: 'GET' });
  },
  getAttendanceReport: async (filters = {}) => {
    if (REPORTS_API_USE_MOCK_DATA) return reportsApiMocks.getAttendanceReport(filters);
    const url = getApiUrlWithParams('attendance-report', {
      startDate: filters.startDate,
      endDate: filters.endDate,
      employee_id: filters.employee_id ?? 'all',
    });
    return apiRequest(url, { method: 'GET' });
  },
  getExpenseReport: async (filters = {}) => {
    if (REPORTS_API_USE_MOCK_DATA) return reportsApiMocks.getExpenseReport(filters);
    const url = getApiUrlWithParams('expense-report', {
      startDate: filters.startDate,
      endDate: filters.endDate,
      category_id: filters.category_id ?? 'all',
      project_id: filters.project_id ?? 'all',
      employee_id: filters.employee_id ?? 'all',
      page: filters.page ?? 1,
      per_page: filters.per_page ?? 10,
    });
    return apiRequest(url, { method: 'GET' });
  },
  getExpenseCategoryReport: async (filters = {}) => {
    if (REPORTS_API_USE_MOCK_DATA) return reportsApiMocks.getExpenseCategoryReport(filters);
    const url = getApiUrlWithParams('expense-report/expense-category-report', {
      startDate: filters.startDate,
      endDate: filters.endDate,
      category_id: filters.category_id ?? 'all',
      page: filters.page ?? 1,
      per_page: filters.per_page ?? 10,
    });
    return apiRequest(url, { method: 'GET' });
  },
  getLeadReportChart: async (filters = {}) => {
    if (REPORTS_API_USE_MOCK_DATA) return reportsApiMocks.getLeadReportChart(filters);
    const year =
      filters.year != null && String(filters.year).trim() !== ''
        ? String(filters.year).trim()
        : '1405';
    const url = getApiUrlWithParams('lead-report', {
      tab: 'chart',
      pipeline: filters.pipeline ?? 'all',
      category: filters.category ?? 'all',
      year,
    });
    return apiRequest(url, { method: 'GET' });
  },
  getLeadReportList: async (filters = {}) => {
    if (REPORTS_API_USE_MOCK_DATA) return reportsApiMocks.getLeadReportList(filters);
    const url = getApiUrlWithParams('lead-report', {
      startDate: filters.startDate,
      endDate: filters.endDate,
      agent: filters.agent ?? 'all',
      page: filters.page ?? 1,
      per_page: filters.per_page ?? 15,
    });
    return apiRequest(url, { method: 'GET' });
  },
  exportLeadReport: async (filters = {}) => {
    if (REPORTS_API_USE_MOCK_DATA) {
      reportsMockExportNotice();
      return;
    }
    const year =
      filters.year != null && String(filters.year).trim() !== ''
        ? String(filters.year).trim()
        : '1405';
    const url = getApiUrlWithParams('deal-report/export', {
      pipeline: filters.pipeline ?? 'all',
      category: filters.category ?? 'all',
      year,
    });
    return downloadExportFile(url, { filenameFallback: 'lead-report.xlsx' });
  },
  getSalesReport: async (filters = {}) => {
    if (REPORTS_API_USE_MOCK_DATA) return reportsApiMocks.getSalesReport(filters);
    const url = getApiUrlWithParams('sales-report', {
      startDate: filters.startDate,
      endDate: filters.endDate,
      client_id: filters.client_id ?? 'all',
      page: filters.page ?? 1,
      per_page: filters.per_page ?? 15,
    });
    return apiRequest(url, { method: 'GET' });
  },
  exportSalesReport: async (filters = {}) => {
    if (REPORTS_API_USE_MOCK_DATA) {
      reportsMockExportNotice();
      return;
    }
    const url = getApiUrlWithParams('sales-report/export', {
      startDate: filters.startDate,
      endDate: filters.endDate,
      client_id: filters.client_id ?? 'all',
    });
    return downloadExportFile(url, { filenameFallback: 'sales-report.xlsx' });
  },
  /** تنظیمات هوش فروش / امتیازدهی کارشناس */
  getSettings: async () => {
    const url = getApiUrl('reports/settings');
    return apiRequest(url, { method: 'GET' });
  },
  updateSettings: async (payload) => {
    const url = getApiUrl('reports/settings');
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  /** داشبورد کلان مدیر */
  getExecutiveDashboard: async (params = {}) => {
    const query = {};
    if (params.preset) query.preset = params.preset;
    if (params.start_date) query.start_date = params.start_date;
    if (params.end_date) query.end_date = params.end_date;
    const url = getApiUrlWithParams('reports/dashboard/executive', query);
    return apiRequest(url, { method: 'GET' });
  },
  /** داشبورد عملیاتی مدیر فروش */
  getOperationsDashboard: async (params = {}) => {
    const query = {};
    if (params.pipeline_id != null && params.pipeline_id !== '') {
      query.pipeline_id = params.pipeline_id;
    }
    if (params.owner_id != null && params.owner_id !== '') {
      query.owner_id = params.owner_id;
    } else if (params.agent_id != null && params.agent_id !== '') {
      query.owner_id = params.agent_id;
    } else if (params.user_id != null && params.user_id !== '') {
      query.owner_id = params.user_id;
    }
    if (params.source_id != null && params.source_id !== '') {
      query.source_id = params.source_id;
    } else if (params.source != null && params.source !== '') {
      query.source_id = params.source;
    }
    if (params.tag_id != null && params.tag_id !== '') {
      query.tag_id = params.tag_id;
    } else if (params.tag != null && params.tag !== '') {
      query.tag_id = params.tag;
    }
    const url = getApiUrlWithParams('reports/dashboard/operations', query);
    return apiRequest(url, { method: 'GET' });
  },
  /** تحلیل دلایل باخت معامله */
  getLostAnalysis: async (params = {}) => {
    const query = {};
    if (params.preset) query.preset = params.preset;
    if (params.start_date) query.start_date = params.start_date;
    if (params.end_date) query.end_date = params.end_date;
    if (params.pipeline_id != null && params.pipeline_id !== '') {
      query.pipeline_id = params.pipeline_id;
    }
    if (params.owner_id != null && params.owner_id !== '') {
      query.owner_id = params.owner_id;
    } else if (params.member_id != null && params.member_id !== '') {
      query.owner_id = params.member_id;
    } else if (params.agent_id != null && params.agent_id !== '') {
      query.owner_id = params.agent_id;
    }
    const url = getApiUrlWithParams('reports/lost-analysis', query);
    return apiRequest(url, { method: 'GET' });
  },
  /** ثبت معاملات باخته به‌عنوان معامله جدید */
  convertLostDealsToNewDeals: async (payload = {}) => {
    const url = getApiUrl('reports/lost-analysis/convert-to-new-deals');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  /** گزارش عملیات پیگیری‌ها و معاملات رهاشده */
  getFollowUpsAnalytics: async () => {
    const url = getApiUrl('reports/follow-ups');
    return apiRequest(url, { method: 'GET' });
  },
  /** تحلیل قیف فروش و نرخ تبدیل (با فیلترهای ترکیبی آنی) */
  getFunnelAnalytics: async (params = {}) => {
    const query = {};
    const pass = [
      'pipeline_id',
      'user_id',
      'team_id',
      'product_id',
      'source_id',
      'campaign_id',
      'tag_id',
      'preset',
      'period_preset',
      'start_date',
      'end_date',
    ];
    pass.forEach((key) => {
      const val = params[key];
      if (val != null && val !== '') query[key] = val;
    });
    const url = getApiUrlWithParams('reports/funnel', query);
    return apiRequest(url, { method: 'GET' });
  },
  /** گزینه‌های فیلتر قیف فروش (کارشناس، تیم، محصول، منبع، کمپین، بازه) */
  getFunnelFilters: async () => {
    const url = getApiUrl('reports/funnel/filters');
    return apiRequest(url, { method: 'GET' });
  },
  /** مقایسه دو سگمنت/بازه قیف فروش */
  compareFunnels: async (payload = {}) => {
    const url = getApiUrl('reports/funnel/compare');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  /** تابلوی رتبه‌بندی کارشناسان فروش */
  getAgentsLeaderboard: async (params = {}) => {
    const query = {};
    if (params.preset) query.preset = params.preset;
    if (params.start_date) query.start_date = params.start_date;
    if (params.end_date) query.end_date = params.end_date;
    if (params.ranking_by) query.ranking_by = params.ranking_by;
    if (params.sort_by) query.sort_by = params.sort_by;
    const url = getApiUrlWithParams('reports/agents/leaderboard', query);
    return apiRequest(url, { method: 'GET' });
  },
  /** ماتریس عملکرد و کارت امتیاز کارشناسان */
  getAgentsPerformance: async (params = {}) => {
    const query = {};
    if (params.preset) query.preset = params.preset;
    if (params.start_date) query.start_date = params.start_date;
    if (params.end_date) query.end_date = params.end_date;
    if (params.owner_id != null && params.owner_id !== '') query.owner_id = params.owner_id;
    if (params.agent_id != null && params.agent_id !== '') query.agent_id = params.agent_id;
    if (params.team_id != null && params.team_id !== '') query.team_id = params.team_id;
    const url = getApiUrlWithParams('reports/agents/performance', query);
    return apiRequest(url, { method: 'GET' });
  },
  /** گزارش فعالیت شخصی کارشناس: تماس ورودی/خروجی، مدت مکالمه، پیگیری‌ها و فروش موفق */
  getMyActivity: async (params = {}) => {
    const query = {};
    if (params.preset) query.preset = params.preset;
    if (params.start_date) query.start_date = params.start_date;
    if (params.end_date) query.end_date = params.end_date;
    if (params.user_id != null && params.user_id !== '') query.user_id = params.user_id;
    const url = getApiUrlWithParams('reports/my-activity', query);
    return apiRequest(url, { method: 'GET' });
  },
  /** سرعت پاسخگویی به سرنخ (از تخصیص تا اولین تماس مالک) */
  getResponseTime: async (params = {}) => {
    const query = {};
    if (params.preset) query.preset = params.preset;
    if (params.start_date) query.start_date = params.start_date;
    if (params.end_date) query.end_date = params.end_date;
    if (params.owner_id != null && params.owner_id !== '') {
      query.owner_id = params.owner_id;
    } else if (params.agent_id != null && params.agent_id !== '') {
      query.owner_id = params.agent_id;
    }
    const url = getApiUrlWithParams('reports/response-time', query);
    return apiRequest(url, { method: 'GET' });
  },
  /** داشبورد اهداف تیم و کارشناسان */
  getGoalsDashboard: async (params = {}) => {
    const query = {};
    if (params.preset) query.preset = params.preset;
    if (params.ranking_by) query.ranking_by = params.ranking_by;
    if (params.sort_by) query.sort_by = params.sort_by;
    if (params.team_id != null && params.team_id !== '' && params.team_id !== 'all') {
      query.team_id = params.team_id;
    }
    if (params.goal_id != null && params.goal_id !== '') {
      query.goal_id = params.goal_id;
    }
    const url = getApiUrlWithParams('reports/goals', query);
    return apiRequest(url, { method: 'GET' });
  },
  /** لیست تیم‌های فروش، اعضا و پیشرفت اهداف دوره جاری */
  getGoalTeams: async (params = {}) => {
    const query = {};
    if (params.period_key) query.period_key = params.period_key;
    if (params.preset) query.preset = params.preset;
    const url = getApiUrlWithParams('reports/goals/teams', query);
    return apiRequest(url, { method: 'GET' });
  },
  /** همه اهداف یک تیم خاص (+ concurrent_goals) برای مودال مدیریت اهداف */
  getTeamGoals: async (teamId, params = {}) => {
    const query = {};
    if (params.period_key) query.period_key = params.period_key;
    const url = getApiUrlWithParams(`reports/goals/teams/${teamId}/goals`, query);
    return apiRequest(url, { method: 'GET' });
  },
  /** همه اهداف کلان سازمان (team_id=null) */
  getCompanyGoals: async (params = {}) => {
    const query = {};
    if (params.period_key) query.period_key = params.period_key;
    const url = getApiUrlWithParams('reports/goals/company-goals', query);
    return apiRequest(url, { method: 'GET' });
  },
  /** ایجاد یا ویرایش تیم فروش و انتساب کارشناسان */
  saveGoalTeam: async (payload = {}) => {
    const url = getApiUrl('reports/goals/teams');
    return apiRequest(url, { method: 'POST', body: JSON.stringify(payload) });
  },
  /** حذف تیم فروش */
  deleteGoalTeam: async (teamId) => {
    const url = getApiUrl(`reports/goals/teams/${teamId}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  /** لیست بازه‌های زمانی هدف (آماده و سفارشی) */
  getGoalPeriodPresets: async () => {
    const url = getApiUrl('reports/goals/period-presets');
    return apiRequest(url, { method: 'GET' });
  },
  /** هدف اختصاصی کارشناس لاگین‌شده (با امکان انتخاب goal_id / preset افق زمانی) */
  getMyGoal: async (params = {}) => {
    const query = {};
    if (params.goal_id != null && params.goal_id !== '') query.goal_id = params.goal_id;
    if (params.preset) query.preset = params.preset;
    if (params.period_type) query.period_type = params.period_type;
    const url = getApiUrlWithParams('reports/goals/my-goal', query);
    return apiRequest(url, { method: 'GET' });
  },
  /** صفحه هدف یک کارشناس مشخص */
  getAgentGoal: async (agentId, params = {}) => {
    const query = {};
    if (params.goal_id != null && params.goal_id !== '') query.goal_id = params.goal_id;
    if (params.preset) query.preset = params.preset;
    if (params.period_type) query.period_type = params.period_type;
    const url = getApiUrlWithParams(`reports/goals/agents/${agentId}`, query);
    return apiRequest(url, { method: 'GET' });
  },
  /** جزییات یک هدف برای فرم ویرایش (fallback وقتی دادهٔ agents.goal در دسترس نیست) */
  getGoalById: async (goalId) => {
    const url = getApiUrl(`reports/goals/${goalId}`);
    return apiRequest(url, { method: 'GET' });
  },
  /** بررسی بلادرنگ هدف تکراری قبل از ثبت */
  checkGoalDuplicate: async (payload = {}) => {
    const url = getApiUrl('reports/goals/check-duplicate');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  /** پیشنهادات هوشمند اهداف (تیمی یا فردی با user_id) */
  getGoalSuggestions: async (params = {}) => {
    const query = {};
    if (params.strategy) query.strategy = params.strategy;
    if (params.growth_rate != null && params.growth_rate !== '') {
      query.growth_rate = params.growth_rate;
    }
    if (params.user_id != null && params.user_id !== '') query.user_id = params.user_id;
    if (params.team_id != null && params.team_id !== '' && params.team_id !== 'all') {
      query.team_id = params.team_id;
    }
    if (params.period_type) query.period_type = params.period_type;
    if (params.start_date) query.start_date = params.start_date;
    if (params.end_date) query.end_date = params.end_date;
    const url = getApiUrlWithParams('reports/goals/suggestions', query);
    return apiRequest(url, { method: 'GET' });
  },
  /** ایجاد یا به‌روزرسانی یک هدف */
  saveGoal: async (payload) => {
    const url = getApiUrl('reports/goals');
    return apiRequest(url, { method: 'POST', body: JSON.stringify(payload) });
  },
  /** ثبت دسته‌جمعی اهداف تیم و کارشناسان */
  saveGoalsBatch: async (payload) => {
    const url = getApiUrl('reports/goals/batch');
    return apiRequest(url, { method: 'POST', body: JSON.stringify(payload) });
  },
  /** حذف یک هدف */
  deleteGoal: async (goalId) => {
    const url = getApiUrl(`reports/goals/${goalId}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

// تعطیلات
export const holidaysApi = {
  getAll: async (filters = {}, requestOptions = {}) => {
    const params = {
      fields: 'occassion,date,department_id_json,designation_id_json,employment_type_json,id',
    };
    const { occassion, month, year, day } = filters;
    const queryFilters = [];
    if (occassion) {
      queryFilters.push(`occassion eq ${occassion}`);
    }
    if (month) {
      queryFilters.push(`month eq ${padJalaliFilterPart(month)}`);
    }
    if (year) {
      queryFilters.push(`year eq ${year}`);
    }
    if (day) {
      queryFilters.push(`day eq ${padJalaliFilterPart(day)}`);
    }
    if (queryFilters.length > 0) {
      params.filters = queryFilters.join(' and ');
    }
    const url = getApiUrlWithParams('holidays', params);
    return apiRequest(url, { method: 'GET', ...requestOptions });
  },
  getById: async (id) => {
    const params = {
      fields: 'occassion,date,department_id_json,designation_id_json,employment_type_json,id',
    };
    const url = getApiUrlWithParams(`holidays/${id}`, params);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('holidays');
    return apiRequest(url, { method: 'POST', body: JSON.stringify(data) });
  },
  update: async (id, data) => {
    const url = getApiUrl(`holidays/${id}`);
    return apiRequest(url, { method: 'PUT', body: JSON.stringify(data) });
  },
  delete: async (id) => {
    const url = getApiUrl(`holidays/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  // عملیات گروهی روی تعطیلات
  applyQuickAction: async (actionType, rowIds) => {
    const url = getApiUrl('holidays/apply-quick-action');
    const payload = {
      action_type: actionType,
      row_ids: Array.isArray(rowIds) ? rowIds.join(',') : rowIds,
    };
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  exportExcel: async ({ year, month } = {}) => {
    const params = {};
    if (year) params.year = year;
    if (month != null) params.month = padJalaliFilterPart(month);
    const url = getApiUrlWithParams('holidays/export', params);
    return downloadExportFile(url, { filenameFallback: 'holidays.xlsx' });
  },
  markHolidayStore: async (data) => {
    const url = getApiUrl('holidays/mark-holiday-store');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// حضور و غیاب
export const attendanceApi = {
  getAll: async ({ employee_id = 'all', year, month, department = 'all', designation = 'all', userId = 'all' } = {}) => {
    const params = {
      employee_id,
      year,
      month: month != null ? padJalaliFilterPart(month) : month,
      department,
      designation,
      userId,
    };
    const url = getApiUrlWithParams('attendances', params);
    return apiRequest(url, { method: 'GET' });
  },
  getByMember: async ({ userId, year, month }) => {
    const params = {
      userId,
      year,
      month: month != null ? padJalaliFilterPart(month) : month,
    };
    const url = getApiUrlWithParams('attendances/by-member', params);
    return apiRequest(url, { method: 'GET' });
  },
  getSummaryMetrics: async ({ userId, year, month } = {}) => {
    const params = {};
    if (userId != null && userId !== '' && userId !== 'all') params.userId = userId;
    if (year != null) params.year = year;
    if (month != null) params.month = padJalaliFilterPart(month);
    const url = getApiUrlWithParams('attendances/summary-metrics', params);
    return apiRequest(url, { method: 'GET' });
  },
  getByHour: async ({ userId = 'all', department = 'all', designation = 'all', month, year }) => {
    const params = { userId, department, designation, month, year };
    const url = getApiUrlWithParams('attendances/by-hour', params);
    return apiRequest(url, { method: 'GET' });
  },
  getNeedsReview: async ({
    type = 'all',
    year,
    month,
    start_date,
    end_date,
    department_id,
    userId,
    page = 1,
    per_page = 15,
  } = {}) => {
    const params = { type, page, per_page };
    if (year != null) params.year = year;
    if (month != null) params.month = padJalaliFilterPart(month);
    if (start_date) params.start_date = start_date;
    if (end_date) params.end_date = end_date;
    if (department_id != null && department_id !== '' && department_id !== 'all') {
      params.department_id = department_id;
    }
    if (userId != null && userId !== '' && userId !== 'all') params.userId = userId;
    const url = getApiUrlWithParams('attendances/needs-review', params);
    return apiRequest(url, { method: 'GET' });
  },
  getNeedsReviewSummary: async ({
    year,
    month,
    start_date,
    end_date,
    department_id,
    userId,
  } = {}) => {
    const params = {};
    if (year != null) params.year = year;
    if (month != null) params.month = padJalaliFilterPart(month);
    if (start_date) params.start_date = start_date;
    if (end_date) params.end_date = end_date;
    if (department_id != null && department_id !== '' && department_id !== 'all') {
      params.department_id = department_id;
    }
    if (userId != null && userId !== '' && userId !== 'all') params.userId = userId;
    const url = getApiUrlWithParams('attendances/needs-review/summary', params);
    return apiRequest(url, { method: 'GET' });
  },
  checkHalfDay: async (data) => {
    const url = getApiUrl('attendances/check-half-day');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  bulkMark: async (data) => {
    const url = getApiUrl('attendances/bulk-mark');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  getById: async (id) => {
    const url = getApiUrl(`attendances/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  /**
   * تعدیل دستی تردد با ثبت الزامی دلیل (ممیزی) —
   * تردد خام اصلی overwrite نمی‌شود.
   */
  adjust: async (id, payload = {}) => {
    const url = getApiUrl(`attendances/${id}/adjust`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  /** تاریخچه کامل ممیزی یک تردد */
  getAdjustments: async (id) => {
    const url = getApiUrl(`attendances/${id}/adjustments`);
    return apiRequest(url, { method: 'GET' });
  },
  /** گزارش جامع ممیزی کل تعدیلات تردد */
  getAdjustmentsReport: async (params = {}) => {
    const query = {};
    if (params.user_id != null && params.user_id !== '' && params.user_id !== 'all') {
      query.user_id = params.user_id;
    }
    if (params.changed_by != null && params.changed_by !== '' && params.changed_by !== 'all') {
      query.changed_by = params.changed_by;
    }
    if (params.source) query.source = params.source;
    if (params.start_date) query.start_date = params.start_date;
    if (params.end_date) query.end_date = params.end_date;
    if (params.page != null) query.page = params.page;
    if (params.per_page != null) query.per_page = params.per_page;
    const url = getApiUrlWithParams('attendance-adjustments', query);
    return apiRequest(url, { method: 'GET' });
  },
  delete: async (id) => {
    const url = getApiUrl(`attendances/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  getEmployeesByDepartment: async (departmentId) => {
    const url = getApiUrl(`employees/by-department/${departmentId}`);
    return apiRequest(url, { method: 'GET' });
  },
  exportExcel: async ({ year, month, id = 'all', department = 'all', designation = 'all' } = {}) => {
    const params = {
      id,
      department,
      designation,
    };
    if (year) params.year = year;
    if (month != null) params.month = padJalaliFilterPart(month);
    const url = getApiUrlWithParams('attendances/export', params);
    return downloadExportFile(url, { filenameFallback: 'attendances.xlsx' });
  },
  getToday: async (requestOptions = {}) => {
    const url = getApiUrl('attendances/today');
    return apiRequest(url, { method: 'GET', ...requestOptions });
  },
  getClockInModal: async () => {
    const url = getApiUrl('attendances/clock-in-modal');
    return apiRequest(url, { method: 'GET' });
  },
  clockIn: async (payload) => {
    const url = getApiUrl('attendances/clock-in');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  clockOut: async (attendanceId, payload) => {
    const url = getApiUrl(`attendances/${attendanceId}/clock-out`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

// درخواست اصلاح تردد / ثبت خروج فراموش‌شده
export const attendanceRegularizationsApi = {
  getAll: async (params = {}) => {
    const query = {};
    if (params.tab) query.tab = params.tab;
    if (params.status) query.status = params.status;
    if (params.user_id != null && params.user_id !== '') query.user_id = params.user_id;
    if (params.start_date) query.start_date = params.start_date;
    if (params.end_date) query.end_date = params.end_date;
    if (params.year != null) query.year = params.year;
    if (params.month != null) query.month = padJalaliFilterPart(params.month);
    if (params.page != null) query.page = params.page;
    if (params.per_page != null) query.per_page = params.per_page;
    const url = getApiUrlWithParams('attendance-regularizations', query);
    return apiRequest(url, { method: 'GET' });
  },
  getCounts: async () => {
    const url = getApiUrl('attendance-regularizations/counts');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (payload) => {
    const url = getApiUrl('attendance-regularizations');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  managerApprove: async (id, payload = {}) => {
    const url = getApiUrl(`attendance-regularizations/${id}/manager-approve`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  hrApprove: async (id, payload = {}) => {
    const url = getApiUrl(`attendance-regularizations/${id}/hr-approve`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  reject: async (id, payload) => {
    const url = getApiUrl(`attendance-regularizations/${id}/reject`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  cancel: async (id) => {
    const url = getApiUrl(`attendance-regularizations/${id}/cancel`);
    return apiRequest(url, { method: 'POST', body: JSON.stringify({}) });
  },
};

/** درخواست و مدیریت اضافه‌کاری (۳-way: درخواست / واقعی / مصوب HR) */
export const overtimeRequestsApi = {
  getAll: async (params = {}) => {
    const query = {};
    if (params.tab) query.tab = params.tab;
    if (params.status) query.status = params.status;
    if (params.user_id != null && params.user_id !== '' && params.user_id !== 'all') {
      query.user_id = params.user_id;
    }
    if (params.year != null) query.year = params.year;
    if (params.month != null) query.month = padJalaliFilterPart(params.month);
    if (params.page != null) query.page = params.page;
    if (params.per_page != null) query.per_page = params.per_page;
    const url = getApiUrlWithParams('overtime-requests', query);
    return apiRequest(url, { method: 'GET' });
  },
  getCounts: async () => {
    const url = getApiUrl('overtime-requests/counts');
    return apiRequest(url, { method: 'GET' });
  },
  /** پیش‌محاسبه ساعت واقعی اضافه‌کاری از روی تردد (date = YYYY-MM-DD) */
  calculateActual: async (date) => {
    const url = getApiUrlWithParams('overtime-requests/calculate-actual', { date });
    return apiRequest(url, { method: 'GET' });
  },
  getById: async (id) => {
    const url = getApiUrl(`overtime-requests/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (payload) => {
    const url = getApiUrl('overtime-requests');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  managerApprove: async (id, payload = {}) => {
    const url = getApiUrl(`overtime-requests/${id}/manager-approve`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  hrApprove: async (id, payload = {}) => {
    const url = getApiUrl(`overtime-requests/${id}/hr-approve`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  reject: async (id, payload) => {
    const url = getApiUrl(`overtime-requests/${id}/reject`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  cancel: async (id) => {
    const url = getApiUrl(`overtime-requests/${id}/cancel`);
    return apiRequest(url, { method: 'POST', body: JSON.stringify({}) });
  },
};

/** درخواست و مدیریت مأموریت (روزانه/ساعتی — درون/برون شهری) */
export const attendanceMissionsApi = {
  getAll: async (params = {}) => {
    const query = {};
    if (params.view_type) query.view_type = params.view_type;
    if (params.tab) query.view_type = params.tab;
    if (params.status) query.status = params.status;
    if (params.type) query.type = params.type;
    if (params.trip_type) query.trip_type = params.trip_type;
    if (params.user_id != null && params.user_id !== '' && params.user_id !== 'all') {
      query.user_id = params.user_id;
    }
    if (params.year != null) query.year = params.year;
    if (params.month != null) query.month = padJalaliFilterPart(params.month);
    if (params.page != null) query.page = params.page;
    if (params.per_page != null) query.per_page = params.per_page;
    if (params.limit != null) query.limit = params.limit;
    const url = getApiUrlWithParams('attendance-missions', query);
    return apiRequest(url, { method: 'GET' });
  },
  getCounts: async () => {
    const url = getApiUrl('attendance-missions/counts');
    return apiRequest(url, { method: 'GET' });
  },
  getById: async (id) => {
    const url = getApiUrl(`attendance-missions/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  /** ثبت با multipart/form-data (فایل پیوست اختیاری) */
  create: async (formData) => {
    const url = getApiUrl('attendance-missions');
    return apiRequest(url, { method: 'POST', body: formData });
  },
  downloadAttachment: async (id, filenameFallback = 'mission-attachment') => {
    const url = getApiUrl(`attendance-missions/${id}/download`);
    return downloadExportFile(url, { filenameFallback });
  },
  managerApprove: async (id, payload = {}) => {
    const url = getApiUrl(`attendance-missions/${id}/manager-approve`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  hrApprove: async (id, payload = {}) => {
    const url = getApiUrl(`attendance-missions/${id}/hr-approve`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  reject: async (id, payload) => {
    const url = getApiUrl(`attendance-missions/${id}/reject`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  cancel: async (id) => {
    const url = getApiUrl(`attendance-missions/${id}/cancel`);
    return apiRequest(url, { method: 'POST', body: JSON.stringify({}) });
  },
};

/**
 * موتور عمومی گردش تأییدات ERP (مرخصی، اضافه‌کاری، اصلاح تردد، ...)
 * — نه کد مخصوص یک ماژول
 */
export const workflowApi = {
  getPendingApprovals: async (params = {}) => {
    const query = {};
    if (params.module_type) query.module_type = params.module_type;
    if (params.date) query.date = params.date;
    if (params.page != null) query.page = params.page;
    if (params.per_page != null) query.per_page = params.per_page;
    const url = getApiUrlWithParams('workflow/pending-approvals', query);
    return apiRequest(url, { method: 'GET' });
  },
  getInstance: async (instanceId) => {
    const url = getApiUrl(`workflow/instances/${instanceId}`);
    return apiRequest(url, { method: 'GET' });
  },
  approve: async (instanceId, payload = {}) => {
    const url = getApiUrl(`workflow/instances/${instanceId}/approve`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  reject: async (instanceId, payload = {}) => {
    const url = getApiUrl(`workflow/instances/${instanceId}/reject`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  cancel: async (instanceId, payload = {}) => {
    const url = getApiUrl(`workflow/instances/${instanceId}/cancel`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  },
};

// شیفت‌ها
export const shiftsApi = {
  getAll: async ({ userId = 'all', department = 'all', view_type = 'week', year, month, week_start_date } = {}) => {
    const params = {
      userId,
      department,
      view_type,
    };
    if (year) params.year = year;
    if (month) params.month = month;
    if (week_start_date) params.week_start_date = week_start_date;
    const url = getApiUrlWithParams('shifts', params);
    return apiRequest(url, { method: 'GET' });
  },
  getById: async (id) => {
    const url = getApiUrl(`shifts/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('shifts');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    const url = getApiUrl(`shifts/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`shifts/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  bulkShift: async (data) => {
    const url = getApiUrl('shifts/bulk-shift');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  getMark: async ({ userid, day, month, year }) => {
    const params = { userid, day, month, year };
    const url = getApiUrlWithParams('shift/mark', params);
    return apiRequest(url, { method: 'GET' });
  },
  exportExcel: async ({ viewType = 'week', startDate, id = 'all', department = 'all' } = {}) => {
    const params = { viewType, id, department };
    if (startDate) {
      params.startDate = String(startDate).replace(/\//g, '-');
    }
    const url = getApiUrlWithParams('shifts/export', params);
    return downloadExportFile(url, { filenameFallback: 'shifts.xlsx' });
  },
  store: async (data) => {
    const url = getApiUrl('shifts');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  getList: async () => {
    const url = getApiUrl('shifts/list');
    return apiRequest(url, { method: 'GET' });
  },
  getChangeRequests: async (filters = {}) => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.employee_id) params.employee_id = filters.employee_id;
    const url = getApiUrlWithParams('shifts/change-requests', params);
    return apiRequest(url, { method: 'GET' });
  },
  approveChangeRequest: async (id) => {
    const url = getApiUrl(`shifts/change-requests/${id}/approve`);
    return apiRequest(url, { method: 'POST' });
  },
  rejectChangeRequest: async (id) => {
    const url = getApiUrl(`shifts/change-requests/${id}/reject`);
    return apiRequest(url, { method: 'POST' });
  },
};

// مرخصی‌ها
export const leavesApi = {
  getAll: async ({ startDate, endDate, employeeId = 'all', leaveTypeId = 'all', status = 'all', name } = {}) => {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (employeeId) params.employeeId = employeeId;
    if (leaveTypeId) params.leaveTypeId = leaveTypeId;
    if (status) params.status = status;
    if (name) params.name = name;
    const url = getApiUrlWithParams('leaves', params);
    return apiRequest(url, { method: 'GET' });
  },
  getCalendar: async ({ start_date, end_date, leave_type = 'all', status = 'all', user_id = 'all', search_text } = {}) => {
    const params = {};
    if (start_date) params.start_date = start_date;
    if (end_date) params.end_date = end_date;
    if (leave_type) params.leave_type = leave_type;
    if (status) params.status = status;
    if (user_id) params.user_id = user_id;
    if (search_text) params.search_text = search_text;
    const url = getApiUrlWithParams('leaves/calendar', params);
    return apiRequest(url, { method: 'GET' });
  },
  getPersonal: async () => {
    const url = getApiUrl('leaves/personal');
    return apiRequest(url, { method: 'GET' });
  },
  getById: async (id) => {
    const url = getApiUrl(`leaves/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('leaves');
    if (data instanceof FormData) {
      return apiRequest(url, { method: 'POST', body: data });
    }
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    const url = getApiUrl(`leaves/${id}`);
    if (data instanceof FormData) {
      // بعضی بک‌اندها برای آپدیت با فایل از POST + _method استفاده می‌کنند؛ فعلاً PUT JSON/FormData
      return apiRequest(url, { method: 'POST', body: data });
    }
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`leaves/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  getEmployeeLeaveTypes: async (userId) => {
    const url = getApiUrl(`employee-leaves/employeeLeaveTypes/${userId}`);
    return apiRequest(url, { method: 'GET' });
  },
  /** پیش‌نمایش محاسبه روزهای کاری / ساعات و کسر سهمیه */
  calculateDays: async (payload) => {
    const url = getApiUrl('leaves/calculate-days');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  // عملیات گروهی روی مرخصی‌ها
  applyQuickAction: async (actionType, rowIds, status = null) => {
    const url = getApiUrl('leaves/apply-quick-action');
    const payload = {
      action_type: actionType,
      row_ids: Array.isArray(rowIds) ? rowIds.join(',') : rowIds,
    };
    if (actionType === 'change-leave-status' && status) {
      payload.status = status;
    }
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /** GET leaves/export — همان فیلترهای لیست مرخصی */
  exportList: async (filters = {}) => {
    const params = {};
    if (filters.startDate) params.startDate = String(filters.startDate).replace(/\//g, '-');
    if (filters.endDate) params.endDate = String(filters.endDate).replace(/\//g, '-');
    if (filters.employeeId != null && filters.employeeId !== '') params.employeeId = filters.employeeId;
    else params.employeeId = 'all';
    if (filters.leaveTypeId != null && filters.leaveTypeId !== '') params.leaveTypeId = filters.leaveTypeId;
    else params.leaveTypeId = 'all';
    if (filters.status != null && filters.status !== '') params.status = filters.status;
    else params.status = 'all';
    if (filters.name !== undefined && filters.name !== null && String(filters.name).trim() !== '') {
      params.name = String(filters.name).trim();
    }
    const url = getApiUrlWithParams('leaves/export', params);
    return downloadExportFile(url, { filenameFallback: 'leaves.xlsx' });
  },
};

// تنظیمات مرخصی
export const leavesSettingsApi = {
  getSettings: async (tab = 'type') => {
    const params = { tab };
    const url = getApiUrlWithParams('leaves-settings', params);
    return apiRequest(url, { method: 'GET' });
  },
  createLeaveType: async (data) => {
    const url = getApiUrl('leaveType/store');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  updateLeaveType: async (id, data) => {
    const url = getApiUrl(`leaveType/update/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteLeaveType: async (id) => {
    const url = getApiUrl(`leaveType/destroy/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  updateGeneralSettings: async (data) => {
    const url = getApiUrl('leaves-settings');
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// تنظیمات حضور و غیاب
export const attendanceSettingsApi = {
  getSettings: async (tab = 'attendance') => {
    const params = { tab };
    const url = getApiUrlWithParams('attendance-settings', params);
    return apiRequest(url, { method: 'GET' });
  },
  updateSettings: async (data) => {
    const url = getApiUrl('attendance-settings');
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  getMonthlyHours: async (year) => {
    const params = {};
    if (year != null) params.year = year;
    const url = getApiUrlWithParams('attendance-settings/monthly-hours', params);
    return apiRequest(url, { method: 'GET' });
  },
  saveMonthlyHours: async (data) => {
    const url = getApiUrl('attendance-settings/monthly-hours');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  loadLaborPreset: async (year) => {
    const url = getApiUrl('attendance-settings/load-labor-preset');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ year: Number(year) }),
    });
  },
};

// شیفت‌های کارمندان
export const employeeShiftsApi = {
  getAll: async () => {
    const url = getApiUrl('employee-shifts');
    return apiRequest(url, { method: 'GET' });
  },
  getById: async (id) => {
    const url = getApiUrl(`employee-shifts/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('employee-shifts');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    const url = getApiUrl(`employee-shifts/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`employee-shifts/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  setDefault: async (shiftId) => {
    const url = getApiUrl('employee-shifts/set-default');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ shift_id: shiftId }),
    });
  },
};

// گردش شیفت‌ها
export const shiftRotationsApi = {
  getAll: async () => {
    const url = getApiUrl('shift-rotations');
    return apiRequest(url, { method: 'GET' });
  },
  getById: async (id) => {
    const url = getApiUrl(`shift-rotations/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('shift-rotations');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    const url = getApiUrl(`shift-rotations/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`shift-rotations/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  storeAutomateShift: async (data) => {
    const url = getApiUrl('shift-rotations/store-automate-shift');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  runRotation: async (data) => {
    const url = getApiUrl('shift-rotations/run-rotation');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  changeStatus: async (data) => {
    const url = getApiUrl('shift-rotations/change-status');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

/** استعلام تکراری بودن شماره تماس (سرنخ / مشتری) */
export const checkPhoneDuplicateApi = async (params = {}) => {
  const query = {};
  if (params.phone) query.phone = params.phone;
  if (params.exclude_lead_id != null && params.exclude_lead_id !== '') {
    query.exclude_lead_id = params.exclude_lead_id;
  }
  if (params.exclude_client_id != null && params.exclude_client_id !== '') {
    query.exclude_client_id = params.exclude_client_id;
  }
  const url = getApiUrlWithParams('check-phone-duplicate', query);
  return apiRequest(url, { method: 'GET' });
};

// تابع‌های CRUD برای سرنخ‌ها (Leads)
export const leadsApi = {
  getAll: async (filters = {}) => {
    const params = {};
    
    // تبدیل فیلترها به query parameters
    if (filters.startDate) params.startDate = formatJalaliApiDate(filters.startDate);
    if (filters.endDate) params.endDate = formatJalaliApiDate(filters.endDate);
    if (filters.category_id) params.category_id = filters.category_id;
    if (filters.source_id) params.source_id = filters.source_id;
    if (filters.owner_id) params.owner_id = filters.owner_id;
    if (filters.success_rate !== undefined) params.success_rate = filters.success_rate;
    if (filters.tags) params.tags = filters.tags;
    if (filters.priority) params.priority = filters.priority;
    if (filters.added_by) params.added_by = filters.added_by;
    if (filters.step) params.step = filters.step;
    if (filters.search) params.search = filters.search;
    if (filters.page) params.page = filters.page;
    if (filters.per_page) params.per_page = filters.per_page;

    const url = getApiUrlWithParams('lead-contacts', params);
    devLog('📤 Leads API URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  getKanbanBoard: async (filters = {}) => {
    const params = {};
    
    // تبدیل فیلترها به query parameters
    if (filters.startDate) params.startDate = formatJalaliApiDate(filters.startDate);
    if (filters.endDate) params.endDate = formatJalaliApiDate(filters.endDate);
    if (filters.category_id) params.category_id = filters.category_id;
    if (filters.source_id) params.source_id = filters.source_id;
    if (filters.owner_id) params.owner_id = filters.owner_id;
    if (filters.added_by) params.added_by = filters.added_by;
    if (filters.success_rate !== undefined) params.success_rate = filters.success_rate;
    if (filters.priority) params.priority = filters.priority;
    if (filters.tags) params.tags = filters.tags;
    else if (filters.tag_id) params.tag_id = filters.tag_id;
    if (filters.search) params.search = filters.search;

    const url = getApiUrlWithParams('kanban-board', params);
    devLog('📤 Kanban Board API URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  loadMoreKanban: async (filters = {}) => {
    const params = { ...filters };
    if (params.startDate) params.startDate = formatJalaliApiDate(params.startDate);
    if (params.endDate) params.endDate = formatJalaliApiDate(params.endDate);
    const url = getApiUrlWithParams('kanban-board/load-more', params);
    devLog('📤 Kanban Board Load More URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  getById: async (id, tab = 'follow-up') => {
    const params = { tab };
    const url = getApiUrlWithParams(`lead-contacts/${id}`, params);
    devLog('📤 Lead By ID URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  updateSingleField: async (id, field, value) => {
    const url = getApiUrl(`lead-contact/update-single-field/${id}`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ field, value }),
    });
  },

  changeStep: async (leadId, step, rejectPayload) => {
    const url = getApiUrl('lead-contact/change-step');
    const body = { lead_id: leadId, step };
    if (typeof rejectPayload === 'string') {
      if (String(rejectPayload).trim() !== '') {
        body.reject_reason = String(rejectPayload).trim();
      }
    } else if (rejectPayload && typeof rejectPayload === 'object') {
      if (rejectPayload.reject_reason_id != null && rejectPayload.reject_reason_id !== '') {
        body.reject_reason_id = Number(rejectPayload.reject_reason_id);
      }
      if (rejectPayload.reject_reason != null && String(rejectPayload.reject_reason).trim() !== '') {
        body.reject_reason = String(rejectPayload.reject_reason).trim();
      }
    }
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  create: async (data) => {
    const url = getApiUrl('lead-contacts');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id, data) => {
    const url = getApiUrl(`lead-contacts/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id) => {
    const url = getApiUrl(`lead-contacts/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  // Lead Notes APIs
  createNote: async (data) => {
    const url = getApiUrl('lead-note/store');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getNote: async (noteId) => {
    const url = getApiUrl(`lead-note/edit/${noteId}`);
    return apiRequest(url, { method: 'GET' });
  },

  updateNote: async (data) => {
    const url = getApiUrl('lead-note/update');
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteNote: async (noteId) => {
    const url = getApiUrl(`lead-note/destroy/${noteId}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  // Lead Follow-up APIs
  getFollowUpCreateData: async () => {
    const url = getApiUrl('lead-contact/create-follow-up');
    return apiRequest(url, { method: 'GET' });
  },

  createFollowUp: async (data) => {
    const url = getApiUrl('lead-contact/store-follow-up');
    const formData = new FormData();
    const isDoNow = data.do_now === true || data.do_now === 'true';

    if (data.follow_up_activity_id !== undefined) {
      formData.append('follow_up_activity_id', data.follow_up_activity_id);
    }
    if (data.do_now !== undefined) {
      formData.append('do_now', isDoNow ? 'true' : 'false');
    }
    if (data.remark !== undefined) {
      formData.append('remark', data.remark || '');
    }
    if (data.lead_id !== undefined) {
      formData.append('lead_id', data.lead_id);
    }
    if (isDoNow && data.duration !== undefined && data.duration !== null && data.duration !== '') {
      formData.append('duration', String(data.duration));
    }

    if (!isDoNow) {
      if (data.next_follow_up_date) {
        formData.append('next_follow_up_date', data.next_follow_up_date);
      }
      if (data.duration !== undefined) {
        formData.append('duration', data.duration);
      }
      if (data.assign_to !== undefined) {
        formData.append('assign_to', data.assign_to);
      }
      if (data.send_reminder !== undefined) {
        formData.append(
          'send_reminder',
          data.send_reminder === true || data.send_reminder === 'true' || data.send_reminder === 'yes' ? 'yes' : 'no',
        );
      } else {
        formData.append('send_reminder', 'no');
      }
      if (data.send_reminder === true || data.send_reminder === 'true' || data.send_reminder === 'yes') {
        if (data.remind_time !== undefined && data.remind_time !== null) {
          formData.append('remind_time', String(data.remind_time));
        }
        if (data.remind_type) {
          formData.append('remind_type', data.remind_type);
        }
      }
    }

    return apiRequest(url, {
      method: 'POST',
      body: formData,
    });
  },

  getFollowUp: async (followUpId) => {
    const url = getApiUrl(`lead-contact/edit-follow-up/${followUpId}`);
    return apiRequest(url, { method: 'GET' });
  },

  updateFollowUp: async (data) => {
    const url = getApiUrl('lead-contact/update-follow-up');
    const params = new URLSearchParams();
    const isDoNow = data.do_now === true || data.do_now === 'true';

    if (data.id !== undefined) {
      params.append('id', data.id);
    }
    if (data.lead_id !== undefined) {
      params.append('lead_id', data.lead_id);
    }
    if (data.follow_up_activity_id !== undefined) {
      params.append('follow_up_activity_id', data.follow_up_activity_id);
    }
    if (data.status) {
      params.append('status', data.status);
    }
    if (isDoNow) {
      params.append('do_now', 'true');
      if (data.duration !== undefined && data.duration !== null && data.duration !== '') {
        params.append('duration', String(data.duration));
      }
    }

    if (!isDoNow) {
      if (data.next_follow_up_date) {
        params.append('next_follow_up_date', data.next_follow_up_date);
      }
      if (data.duration !== undefined && data.duration !== null) {
        params.append('duration', String(data.duration));
      }
      if (data.assign_to !== undefined && data.assign_to !== null) {
        params.append('assign_to', String(data.assign_to));
      }
      if (data.send_reminder !== undefined) {
        params.append(
          'send_reminder',
          data.send_reminder === true || data.send_reminder === 'true' || data.send_reminder === 'yes' ? 'yes' : 'no',
        );
      }
      if (data.remind_time !== undefined && data.remind_time !== null) {
        params.append('remind_time', String(data.remind_time));
      }
      if (data.remind_type) {
        params.append('remind_type', data.remind_type);
      }
    }

    if (data.remark !== undefined && data.remark !== null) {
      params.append('remark', data.remark);
    }

    return apiRequest(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });
  },

  updateFollowUpRemark: async (id, remark) => {
    const url = getApiUrl('lead-contact/update-follow-up-remark');
    
    const params = new URLSearchParams();
    params.append('id', id);
    params.append('remark', remark);

    return apiRequest(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });
  },

  updateFollowUpStatus: async (id, status) => {
    const url = getApiUrl('lead-contact/update-follow-up-status');
    return apiRequest(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id, status }),
    });
  },

  deleteFollowUp: async (followUpId) => {
    const url = getApiUrl(`lead-contact/destroy-follow-up/${followUpId}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  // عملیات دسته‌جمعی برای سرنخ‌ها
  applyQuickAction: async (actionType, rowIds, extra = {}) => {
    const url = getApiUrl('lead-contacts/apply-quick-action');
    const payload = {
      action_type: actionType,
      row_ids: Array.isArray(rowIds) ? rowIds.join(',') : rowIds,
    };
    if (actionType === 'change-owner' && extra.owner_id != null && extra.owner_id !== '') {
      payload.owner_id = extra.owner_id;
    }
    if (actionType === 'change-step' && extra.step != null && extra.step !== '') {
      payload.step = extra.step;
    }
    if (actionType === 'send-sms') {
      payload.message = extra.message ?? '';
    }
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /** GET lead-contacts/export — type=lead و tag به‌جای tags در صورت نیاز */
  exportList: async (filters = {}) => {
    const params = { type: 'lead' };
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.category_id != null && filters.category_id !== '') params.category_id = filters.category_id;
    if (filters.source_id != null && filters.source_id !== '') params.source_id = filters.source_id;
    if (filters.owner_id != null && filters.owner_id !== '') params.owner_id = filters.owner_id;
    if (
      filters.success_rate !== undefined &&
      filters.success_rate !== null &&
      filters.success_rate !== ''
    ) {
      params.success_rate = filters.success_rate;
    }
    const tagVal = filters.tag !== undefined ? filters.tag : filters.tags;
    if (tagVal !== undefined && tagVal !== null && tagVal !== '') params.tag = tagVal;
    else params.tag = 'all';
    if (filters.priority !== undefined && filters.priority !== null && filters.priority !== '') {
      params.priority = filters.priority;
    }
    if (filters.step !== undefined && filters.step !== null && filters.step !== '') {
      params.step = filters.step;
    } else {
      params.step = 'all';
    }
    if (filters.search) params.search = filters.search;
    const url = getApiUrlWithParams('lead-contacts/export', params);
    return downloadExportFile(url, { filenameFallback: 'leads.xlsx' });
  },

  /** GET lead-contacts/import — متادیتا و فایل نمونه */
  getImportForm: async () => {
    const url = getApiUrl('lead-contacts/import');
    return apiRequest(url, { method: 'GET' });
  },

  /** POST lead-contacts/import — آپلود فایل */
  uploadImportFile: async (file, hasHeading) => {
    const url = getApiUrl('lead-contacts/import');
    const formData = new FormData();
    formData.append('import_file', file);
    formData.append('heading', hasHeading ? 'on' : 'off');
    return apiRequest(url, { method: 'POST', body: formData });
  },

  /** POST lead-contacts/import/process — شروع پردازش */
  processImport: async (payload) => {
    const url = getApiUrl('lead-contacts/import/process');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

/** وضعیت و خطاهای import عمومی (سرنخ، کارمند و ...) */
export const importApi = {
  /** GET /import/process/{batch.name}/{batch.id} */
  getProcessStatus: async (batchName, batchId) => {
    const url = getApiUrl(`import/process/${batchName}/${batchId}`);
    return apiRequest(url, { method: 'GET' });
  },

  getExceptions: async (exceptionKey) => {
    const url = getApiUrl(`import/exception/${exceptionKey}`);
    return apiRequest(url, { method: 'GET' });
  },
};

// تابع‌های CRUD برای معامله‌ها (Deals)
/** GET options/{products,tags,lead-sources} — گزینه‌های صفحه‌بندی‌شده و قابل جستجو برای فیلترها و فرم‌ها */
const fetchOptionList = (endpoint) => async ({ search, page = 1, per_page = 20, ids } = {}) => {
  const params = ids?.length ? { 'ids[]': ids } : { search: search || undefined, page, per_page };
  return apiRequest(getApiUrlWithParams(endpoint, params), { method: 'GET' });
};

export const optionsApi = {
  products: fetchOptionList('options/products'),
  tags: fetchOptionList('options/tags'),
  leadSources: fetchOptionList('options/lead-sources'),
};

export const dealsApi = {
  /**
   * GET deals/lead-options — لیست صفحه‌بندی‌شده و قابل جستجوی سرنخ‌ها برای فیلتر/فرم معامله
   * params: { search, page, per_page } یا { ids: [..] } برای گرفتن برچسب مقادیر انتخاب‌شده
   */
  getLeadOptions: async ({ search, page = 1, per_page = 20, ids } = {}) => {
    const params = ids?.length
      ? { 'ids[]': ids }
      : { search: search || undefined, page, per_page };
    return apiRequest(getApiUrlWithParams('deals/lead-options', params), { method: 'GET' });
  },

  getAll: async (filters = {}) => {
    const params = { ...filters };
    if (params.deal_owner_id == null && params.owner_id != null) {
      params.deal_owner_id = params.owner_id;
      delete params.owner_id;
    }

    const url = getApiUrlWithParams('deals', params);
    devLog('📤 Deals API URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  /** GET deals/export — پارامترها مطابق لیست معامله‌ها (بدون page) */
  exportList: async (filters = {}) => {
    const params = {};
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.pipeline_id != null && filters.pipeline_id !== '') params.pipeline_id = filters.pipeline_id;
    else params.pipeline_id = 'all';
    if (filters.category_id != null && filters.category_id !== '') params.category_id = filters.category_id;
    else params.category_id = 'all';
    if (filters.product_id != null && filters.product_id !== '') params.product_id = filters.product_id;
    else params.product_id = 'all';
    if (filters.deal_owner_id != null && filters.deal_owner_id !== '') {
      params.deal_owner_id = filters.deal_owner_id;
    } else if (filters.owner_id != null && filters.owner_id !== '') {
      params.deal_owner_id = filters.owner_id;
    } else {
      params.deal_owner_id = 'all';
    }
    if (filters.min !== undefined && filters.min !== null && filters.min !== '') params.min = filters.min;
    if (filters.max !== undefined && filters.max !== null && filters.max !== '') params.max = filters.max;
    if (filters.stage_id != null && filters.stage_id !== '') params.stage_id = filters.stage_id;
    else params.stage_id = 'all';
    if (filters.agent_id != null && filters.agent_id !== '') params.agent_id = filters.agent_id;
    else params.agent_id = 'all';
    if (filters.lead_id != null && filters.lead_id !== '') params.lead_id = filters.lead_id;
    else params.lead_id = 'all';
    if (filters.source_id != null && filters.source_id !== '') params.source_id = filters.source_id;
    else params.source_id = 'all';
    if (filters.priority !== undefined && filters.priority !== null && filters.priority !== '') {
      params.priority = filters.priority;
    } else {
      params.priority = 'all';
    }
    if (
      filters.success_rate !== undefined &&
      filters.success_rate !== null &&
      filters.success_rate !== ''
    ) {
      params.success_rate = filters.success_rate;
    }
    // بک‌اند export پارامتر `tag` می‌خواهد؛ لیست معمولاً `tags` می‌سازد
    const tagVal = filters.tag ?? filters.tags;
    if (tagVal != null && tagVal !== '' && tagVal !== 'all') {
      params.tag = Array.isArray(tagVal) ? tagVal.join(',') : String(tagVal);
    } else {
      params.tag = 'all';
    }
    if (filters.date_filter_on) params.date_filter_on = filters.date_filter_on;
    else if (filters.date_by) {
      params.date_filter_on = filters.date_by === 'close_date' ? 'close_date' : 'created_at';
    }
    if (filters.search) params.search = filters.search;
    const url = getApiUrlWithParams('deals/export', params);
    return downloadExportFile(url, { filenameFallback: 'deals.xlsx' });
  },

  /** GET deals/import — متادیتا و فایل نمونه */
  getImportForm: async () => {
    const url = getApiUrl('deals/import');
    return apiRequest(url, { method: 'GET' });
  },

  /** POST deals/import — آپلود فایل */
  uploadImportFile: async (file, hasHeading) => {
    const url = getApiUrl('deals/import');
    const formData = new FormData();
    formData.append('import_file', file);
    formData.append('heading', hasHeading ? 'true' : 'false');
    return apiRequest(url, { method: 'POST', body: formData });
  },

  /** POST deals/import/process — شروع پردازش */
  processImport: async (payload) => {
    const url = getApiUrl('deals/import/process');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getById: async (id, tab = 'follow-up') => {
    // For follow-up tab, don't send tab parameter (default behavior)
    const params = tab && tab !== 'follow-up' ? { tab } : {};
    const url = getApiUrlWithParams(`deals/${id}`, params);
    devLog('📤 Deal By ID URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  updateSingleField: async (id, field, value) => {
    const url = getApiUrl(`deal/update-single-field/${id}`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ field, value }),
    });
  },

  changeStage: async (dealId, stageId, rejectPayload) => {
    const url = getApiUrl('deal/change-stage');
    const payload = { deal_id: dealId, stage_id: stageId };
    if (typeof rejectPayload === 'string') {
      if (String(rejectPayload).trim() !== '') {
        payload.reject_reason = String(rejectPayload).trim();
      }
    } else if (rejectPayload && typeof rejectPayload === 'object') {
      if (rejectPayload.reject_reason_id != null && rejectPayload.reject_reason_id !== '') {
        payload.reject_reason_id = Number(rejectPayload.reject_reason_id);
      }
      if (rejectPayload.reject_reason != null && String(rejectPayload.reject_reason).trim() !== '') {
        payload.reject_reason = String(rejectPayload.reject_reason).trim();
      }
    }
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  changePipeline: async (dealId, pipelineId, stageId, rejectPayload) => {
    const url = getApiUrl('deal/change-pipeline');
    const payload = {
      deal_id: dealId,
      pipeline_id: pipelineId,
      stage_id: stageId,
    };
    if (typeof rejectPayload === 'string') {
      if (String(rejectPayload).trim() !== '') {
        payload.reject_reason = String(rejectPayload).trim();
      }
    } else if (rejectPayload && typeof rejectPayload === 'object') {
      if (rejectPayload.reject_reason_id != null && rejectPayload.reject_reason_id !== '') {
        payload.reject_reason_id = Number(rejectPayload.reject_reason_id);
      }
      if (rejectPayload.reject_reason != null && String(rejectPayload.reject_reason).trim() !== '') {
        payload.reject_reason = String(rejectPayload.reject_reason).trim();
      }
    }
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  create: async (payload) => {
    const url = getApiUrl('deals');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /** GET deals/create — متادیتای فرم ایجاد */
  getCreateForm: async ({ leadId, clientId } = {}) => {
    const params = {};
    if (leadId != null && leadId !== '') params.lead_id = leadId;
    if (clientId != null && clientId !== '') params.client_id = clientId;
    const url = getApiUrlWithParams('deals/create', params);
    return apiRequest(url, { method: 'GET' });
  },

  delete: async (id) => {
    const url = getApiUrl(`deals/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  applyQuickAction: async (actionType, rowIds, extra = {}) => {
    const url = getApiUrl('deals/apply-quick-action');
    const payload = {
      action_type: actionType,
      row_ids: Array.isArray(rowIds) ? rowIds.join(',') : String(rowIds),
    };
    if (actionType === 'change-status' && extra.status != null && extra.status !== '') {
      payload.status = extra.status;
    }
    if (actionType === 'change-deal-agents' && extra.agent != null && extra.agent !== '') {
      payload.agent = extra.agent;
    }
    if (actionType === 'change-pipeline') {
      if (extra.pipeline_id != null && extra.pipeline_id !== '') {
        payload.pipeline_id = extra.pipeline_id;
      }
      if (extra.stage_id != null && extra.stage_id !== '') {
        payload.stage_id = extra.stage_id;
      }
    }
    if (actionType === 'send-sms') {
      payload.message = extra.message ?? '';
    }
    if (actionType === 'change-owner' && extra.owner_id != null && extra.owner_id !== '') {
      payload.owner_id = extra.owner_id;
    }
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getKanbanBoard: async (filters = {}) => {
    const params = { ...filters };
    delete params.pipeline_id;
    delete params.product_id;
    delete params.owner_id;
    delete params.agent_id;
    delete params.tag_id;
    delete params.stage_id;
    delete params.lead_id;

    const url = getApiUrlWithParams('deals-kanban-board', params);
    devLog('📤 Deals Kanban API URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  loadMoreKanban: async (filters = {}) => {
    const params = { ...filters };
    delete params.pipeline_id;
    delete params.product_id;
    delete params.owner_id;
    delete params.agent_id;
    delete params.tag_id;
    delete params.stage_id;
    delete params.lead_id;
    const url = getApiUrlWithParams('deals-kanban-board/load-more', params);
    devLog('📤 Deals Kanban Load More URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  // Deal Notes APIs
  createNote: async (data) => {
    const url = getApiUrl('deal-note/store');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getNote: async (noteId) => {
    const url = getApiUrl(`deal-note/edit/${noteId}`);
    return apiRequest(url, { method: 'GET' });
  },

  updateNote: async (data) => {
    const url = getApiUrl('deal-note/update');
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteNote: async (noteId) => {
    const url = getApiUrl(`deal-note/destroy/${noteId}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  // Deal Follow-up APIs
  getFollowUpCreateData: async () => {
    const url = getApiUrl('deal/create-follow-up');
    return apiRequest(url, { method: 'GET' });
  },

  createFollowUp: async (data) => {
    const url = getApiUrl('deal/store-follow-up');
    const formData = new FormData();
    const isDoNow = data.do_now === true || data.do_now === 'true';

    if (data.follow_up_activity_id !== undefined) {
      formData.append('follow_up_activity_id', data.follow_up_activity_id);
    }
    if (data.do_now !== undefined) {
      formData.append('do_now', isDoNow ? 'true' : 'false');
    }
    if (data.remark !== undefined) {
      formData.append('remark', data.remark || '');
    }
    if (data.deal_id !== undefined) {
      formData.append('deal_id', data.deal_id);
    }
    if (isDoNow && data.duration !== undefined && data.duration !== null && data.duration !== '') {
      formData.append('duration', String(data.duration));
    }

    if (!isDoNow) {
      if (data.next_follow_up_date) {
        formData.append('next_follow_up_date', data.next_follow_up_date);
      }
      if (data.duration !== undefined) {
        formData.append('duration', data.duration);
      }
      if (data.assign_to !== undefined) {
        formData.append('assign_to', data.assign_to);
      }
      if (data.send_reminder !== undefined) {
        formData.append(
          'send_reminder',
          data.send_reminder === true || data.send_reminder === 'true' || data.send_reminder === 'yes' ? 'yes' : 'no',
        );
      } else {
        formData.append('send_reminder', 'no');
      }
      if (data.send_reminder === true || data.send_reminder === 'true' || data.send_reminder === 'yes') {
        if (data.remind_time !== undefined && data.remind_time !== null) {
          formData.append('remind_time', String(data.remind_time));
        }
        if (data.remind_type) {
          formData.append('remind_type', data.remind_type);
        }
      }
    }

    return apiRequest(url, {
      method: 'POST',
      body: formData,
    });
  },

  getFollowUp: async (followUpId) => {
    const url = getApiUrl(`deal/edit-follow-up/${followUpId}`);
    return apiRequest(url, { method: 'GET' });
  },

  updateFollowUp: async (data) => {
    const url = getApiUrl('deal/update-follow-up');
    const params = new URLSearchParams();
    const isDoNow = data.do_now === true || data.do_now === 'true';

    if (data.id !== undefined) {
      params.append('id', data.id);
    }
    if (data.deal_id !== undefined) {
      params.append('deal_id', data.deal_id);
    }
    if (data.follow_up_activity_id !== undefined) {
      params.append('follow_up_activity_id', data.follow_up_activity_id);
    }
    if (data.status) {
      params.append('status', data.status);
    }
    if (isDoNow) {
      params.append('do_now', 'true');
      if (data.duration !== undefined && data.duration !== null && data.duration !== '') {
        params.append('duration', String(data.duration));
      }
    }

    if (!isDoNow) {
      if (data.next_follow_up_date) {
        params.append('next_follow_up_date', data.next_follow_up_date);
      }
      if (data.duration !== undefined && data.duration !== null) {
        params.append('duration', String(data.duration));
      }
      if (data.assign_to !== undefined && data.assign_to !== null) {
        params.append('assign_to', String(data.assign_to));
      }
      if (data.send_reminder !== undefined) {
        params.append(
          'send_reminder',
          data.send_reminder === true || data.send_reminder === 'true' || data.send_reminder === 'yes' ? 'yes' : 'no',
        );
      }
      if (data.remind_time !== undefined && data.remind_time !== null) {
        params.append('remind_time', String(data.remind_time));
      }
      if (data.remind_type) {
        params.append('remind_type', data.remind_type);
      }
    }

    if (data.remark !== undefined && data.remark !== null) {
      params.append('remark', data.remark);
    }

    return apiRequest(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });
  },

  updateFollowUpRemark: async (id, remark) => {
    const url = getApiUrl('deal/update-follow-up-remark');
    
    const params = new URLSearchParams();
    params.append('id', id);
    params.append('remark', remark);

    return apiRequest(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });
  },

  updateFollowUpStatus: async (id, status) => {
    const url = getApiUrl('deal/update-follow-up-status');
    return apiRequest(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id, status }),
    });
  },

  deleteFollowUp: async (followUpId) => {
    const url = getApiUrl(`deal/destroy-follow-up/${followUpId}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  // Deal Files APIs
  uploadFile: async (dealId, files) => {
    const url = getApiUrl('deal-files');
    const formData = new FormData();
    
    formData.append('deal_id', dealId);
    
    // اضافه کردن فایل‌ها به FormData
    if (Array.isArray(files)) {
      files.forEach((file) => {
        formData.append('file[]', file);
      });
    } else {
      formData.append('file[]', files);
    }

    return apiRequest(url, {
      method: 'POST',
      body: formData,
    });
  },

  deleteFile: async (fileId) => {
    const url = getApiUrl(`deal-files/${fileId}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  downloadFile: async (fileId, filename) => {
    const url = getApiUrl(`deal-files/download/${fileId}`);
    return downloadExportFile(url, {
      filenameFallback: filename || 'deal-file',
    });
  },
};


/** دسته‌بندی فرم سرنخ */
export const leadFormCategoriesApi = {
  getCreateData: async () => {
    const url = getApiUrl('lead-form-categories/create');
    return apiRequest(url, { method: 'GET' });
  },
  create: async ({ name, status = 'active' }) => {
    const url = getApiUrl('lead-form-categories');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ name, status }),
    });
  },
  update: async (id, payload) => {
    const url = getApiUrl(`lead-form-categories/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`lead-form-categories/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** فرم‌ساز سرنخ */
export const leadFormsApi = {
  getBuilderIndex: async () => {
    const url = getApiUrl('lead-form-builder');
    return apiRequest(url, { method: 'GET' });
  },
  getCreateData: async () => {
    const url = getApiUrl('lead-forms/create');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (payload) => {
    const url = getApiUrl('lead-forms');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getById: async (id) => {
    const url = getApiUrl(`lead-forms/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  update: async (id, payload) => {
    const url = getApiUrl(`lead-forms/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  updateStatus: async (id, status) => {
    const url = getApiUrl(`lead-forms/${id}/status`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },
  getPreview: async (id) => {
    const url = getApiUrl(`lead-forms/${id}/preview`);
    return apiRequest(url, { method: 'GET' });
  },
  updateAppearance: async (id, payload) => {
    const url = getApiUrl(`lead-forms/${id}/appearance`);
    if (payload instanceof FormData) {
      return apiRequest(url, { method: 'POST', body: payload });
    }
    const formData = new FormData();
    if (payload.background_type) formData.append('background_type', payload.background_type);
    if (payload.background_color) formData.append('background_color', payload.background_color);
    if (payload.background_image) formData.append('background_image', payload.background_image);
    if (payload.remove_background_image === true) {
      formData.append('remove_background_image', 'true');
    }
    if (payload.background_default) {
      formData.append('background_default', payload.background_default);
    }
    return apiRequest(url, { method: 'POST', body: formData });
  },
  getLeads: async (id, { page = 1, per_page = 15 } = {}) => {
    const url = getApiUrlWithParams(`lead-forms/${id}/leads`, { page, per_page });
    return apiRequest(url, { method: 'GET' });
  },
  exportLeads: async (id) => {
    const url = getApiUrl(`lead-forms/${id}/export`);
    return downloadExportFile(url, { filenameFallback: `lead-form-${id}-leads.xlsx` });
  },
};


/** ارسال و تاریخچه پیامک */
export const smsApi = {
  send: async ({ type, id, message: messageText }) => {
    const url = getApiUrl('sms/send');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ type, id, message: messageText }),
    });
  },
  /** تاریخچه پیامک‌های یک معامله */
  getDealHistory: async (dealId) => {
    const url = getApiUrl(`deals/${dealId}/sms-history`);
    return apiRequest(url, { method: 'GET' });
  },
  /** تاریخچه پیامک‌های یک سرنخ */
  getLeadHistory: async (leadId) => {
    const url = getApiUrl(`lead-contacts/${leadId}/sms-history`);
    return apiRequest(url, { method: 'GET' });
  },
  /** تاریخچه پیامک‌های یک مشتری */
  getClientHistory: async (clientId) => {
    const url = getApiUrl(`clients/${clientId}/sms-history`);
    return apiRequest(url, { method: 'GET' });
  },
  /** پیامک‌های ورودی بدون انتساب */
  getUnmatched: async (params = {}) => {
    const query = {};
    if (params.page != null) query.page = params.page;
    if (params.limit != null) query.limit = params.limit;
    if (params.search) query.search = params.search;
    const url = getApiUrlWithParams('sms/unmatched', query);
    return apiRequest(url, { method: 'GET' });
  },
  /** علامت‌گذاری پیام به عنوان خوانده‌شده */
  markRead: async (smsId) => {
    const url = getApiUrl(`sms/${smsId}/mark-read`);
    return apiRequest(url, { method: 'POST' });
  },
};


// Lead Settings API
export const leadSettingsApi = {
  getSettings: async (tab = 'source') => {
    const params = tab !== 'source' ? { tab } : {};
    const url = getApiUrlWithParams('lead-settings', params);
    return apiRequest(url, { method: 'GET' });
  },

  // Lead Source CRUD
  createLeadSource: async (type) => {
    const url = getApiUrl('lead-source-settings');
    return apiRequest(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ type }).toString(),
    });
  },

  updateLeadSource: async (id, type) => {
    const url = getApiUrl(`lead-source-settings/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ type }).toString(),
    });
  },

  deleteLeadSource: async (id) => {
    const url = getApiUrl(`lead-source-settings/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  // Lead Reject Reason CRUD
  createRejectReason: async (name) => {
    const url = getApiUrl('lead-reject-reason-settings');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  updateRejectReason: async (id, name) => {
    const url = getApiUrl(`lead-reject-reason-settings/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  },

  deleteRejectReason: async (id) => {
    const url = getApiUrl(`lead-reject-reason-settings/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  // Lead Category CRUD
  getLeadCategories: async (type = 'lead') => {
    const url = getApiUrlWithParams('lead-category-settings', { type });
    return apiRequest(url, { method: 'GET' });
  },

  getLeadCategory: async (id) => {
    const url = getApiUrl(`lead-category-settings/${id}`);
    return apiRequest(url, { method: 'GET' });
  },

  createLeadCategory: async (type, category_name, parent_id) => {
    const url = getApiUrl('lead-category-settings');
    const params = { type, category_name };
    if (parent_id) {
      params.parent_id = parent_id;
    }
    return apiRequest(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(params).toString(),
    });
  },

  updateLeadCategory: async (id, type, category_name, parent_id) => {
    const url = getApiUrl(`lead-category-settings/${id}`);
    const params = { type, category_name };
    if (parent_id) {
      params.parent_id = parent_id;
    }
    return apiRequest(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(params).toString(),
    });
  },

  deleteLeadCategory: async (id) => {
    const url = getApiUrl(`lead-category-settings/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  // Pipeline CRUD
  getPipelines: async () => {
    const url = getApiUrl('lead-pipeline-settings');
    return apiRequest(url, { method: 'GET' });
  },

  getPipeline: async (id) => {
    const url = getApiUrl(`lead-pipeline-settings/${id}`);
    return apiRequest(url, { method: 'GET' });
  },

  createPipeline: async (name, label_color) => {
    const url = getApiUrl('lead-pipeline-settings');
    return apiRequest(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ name, label_color }).toString(),
    });
  },

  updatePipeline: async (id, name, label_color) => {
    const url = getApiUrl(`lead-pipeline-settings/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ name, label_color }).toString(),
    });
  },

  deletePipeline: async (id) => {
    const url = getApiUrl(`lead-pipeline-settings/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  setDefaultPipeline: async (id) => {
    const url = getApiUrl(`lead-pipeline-settings/set-default/${id}`);
    return apiRequest(url, { method: 'PATCH' });
  },

  // Tags CRUD
  getTags: async () => {
    const url = getApiUrl('tags');
    return apiRequest(url, { method: 'GET' });
  },

  getTag: async (id) => {
    const url = getApiUrl(`tags/${id}`);
    return apiRequest(url, { method: 'GET' });
  },

  createTag: async (name, color) => {
    const url = getApiUrl('tags');
    return apiRequest(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ name, color }).toString(),
    });
  },

  updateTag: async (id, name, color) => {
    const url = getApiUrl(`tags/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ name, color }).toString(),
    });
  },

  deleteTag: async (id) => {
    const url = getApiUrl(`tags/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  // Follow-up Activity CRUD
  getFollowUpActivity: async (id) => {
    const url = getApiUrl(`followup-activity-setting/${id}`);
    return apiRequest(url, { method: 'GET' });
  },

  createFollowUpActivity: async (name, color, icon) => {
    const url = getApiUrl('followup-activity-setting');
    return apiRequest(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ name, color, icon }).toString(),
    });
  },

  updateFollowUpActivity: async (id, name, color, icon) => {
    const url = getApiUrl(`followup-activity-setting/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ name, color, icon }).toString(),
    });
  },

  deleteFollowUpActivity: async (id) => {
    const url = getApiUrl(`followup-activity-setting/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  // Lead Stage CRUD
  getLeadStage: async (id) => {
    const url = getApiUrl(`lead-stage-setting/${id}`);
    return apiRequest(url, { method: 'GET' });
  },

  createLeadStage: async (name, label_color, pipelineId) => {
    const url = getApiUrl('lead-stage-setting');
    const pipeline = Array.isArray(pipelineId)
      ? pipelineId.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
      : [Number(pipelineId)].filter((n) => !Number.isNaN(n));
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({
        name,
        label_color,
        pipeline,
      }),
    });
  },

  updateLeadStage: async (id, name, label_color, priority) => {
    const url = getApiUrl(`lead-stage-setting/${id}`);
    const params = {
      name,
      label_color,
      priority: Number(priority),
    };
    return apiRequest(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(params).toString(),
    });
  },

  deleteLeadStage: async (id) => {
    const url = getApiUrl(`lead-stage-setting/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  setDefaultLeadStage: async (id) => {
    const url = getApiUrl(`lead-stage-setting/set-default/${id}`);
    return apiRequest(url, { method: 'PATCH' });
  },

  // Lead Agent Settings CRUD
  getCreateAgentData: async () => {
    const url = getApiUrl('lead-agent-settings/create');
    return apiRequest(url, { method: 'GET' });
  },

  /** دسته‌بندی‌های قابل تخصیص به ناظر (فقط آن‌هایی که کاربر هنوز ندارد) */
  getAgentCategories: async (userId) => {
    const url = getApiUrl(`agent-category/${userId}`);
    return apiRequest(url, { method: 'GET' });
  },

  createAgent: async (agent_id, category_ids) => {
    const url = getApiUrl('lead-agent-settings');
    const payload = {
      agent_id: agent_id,
      category_id: category_ids || [],
    };
    return apiRequest(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  },

  deleteAgent: async (id) => {
    const url = getApiUrl(`lead-agent-settings/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  updateAgentCategory: async (id, categoryIds) => {
    const url = getApiUrl(`lead-agent-settings/update-category/${id}`);
    const params = new URLSearchParams();
    categoryIds.forEach(catId => {
      params.append('categoryId[]', catId);
    });
    return apiRequest(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });
  },

  updateAgentStatus: async (id, status) => {
    const url = getApiUrl(`lead-agent-settings/update-status/${id}`);
    return apiRequest(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ status }).toString(),
    });
  },

  deleteLeadAgent: async (id) => {
    const url = getApiUrl(`lead-agent-settings/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  updateRoundRobinStatus: async (id, lead_setting_status, userId) => {
    const url = getApiUrl(`lead-settings/update-status/${id}`);
    const payload = {
      lead_setting_status: lead_setting_status,
      userId: userId,
    };
    return apiRequest(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  },
};

// Products API
export const productsApi = {
  getAll: async (filters = {}) => {
    const params = {};
    
    if (filters.category_id) params.category_id = filters.category_id;
    if (filters.sub_category_id) params.sub_category_id = filters.sub_category_id;
    if (filters.unit_type_id) params.unit_type_id = filters.unit_type_id;
    if (filters.search) params.search = filters.search;
    if (filters.page) params.page = filters.page;
    if (filters.per_page) params.per_page = filters.per_page;
    
    const url = getApiUrlWithParams('products', params);
    console.log('📤 Products API URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  getCreateData: async () => {
    const url = getApiUrl('products/create');
    return apiRequest(url, { method: 'GET' });
  },

  getById: async (id) => {
    const url = getApiUrl(`products/${id}`);
    return apiRequest(url, { method: 'GET' });
  },

  create: async (payload) => {
    const url = getApiUrl('products');
    if (!productPayloadHasFiles(payload)) {
      return apiRequest(url, {
        method: 'POST',
        body: JSON.stringify(serializeProductJsonPayload(payload)),
      });
    }
    const formData = new FormData();
    appendProductFormData(formData, payload);
    return apiRequest(url, { method: 'POST', body: formData });
  },

  update: async (id, payload) => {
    const url = getApiUrl(`products/${id}`);
    if (!productPayloadHasFiles(payload)) {
      return apiRequest(url, {
        method: 'PUT',
        body: JSON.stringify(serializeProductJsonPayload(payload)),
      });
    }
    const formData = new FormData();
    appendProductFormData(formData, payload);
    formData.append('_method', 'PUT');
    return apiRequest(url, { method: 'POST', body: formData });
  },

  destroyFile: async (productId) => {
    const url = getApiUrl(`products/destroy-file/${productId}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  exportExcel: async ({ category_id = 'all', sub_category_id = 'all', unit_type_id = 'all' } = {}) => {
    const url = getApiUrlWithParams('products/export', {
      category_id,
      sub_category_id,
      unit_type_id,
    });
    return downloadExportFile(url, { filenameFallback: 'products.xlsx' });
  },

  delete: async (id) => {
    const url = getApiUrl(`products/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  // عملیات گروهی روی محصولات
  applyQuickAction: async (actionType, rowIds, status = null) => {
    const url = getApiUrl('products/apply-quick-action');
    const payload = {
      action_type: actionType,
      row_ids: Array.isArray(rowIds) ? rowIds.join(',') : rowIds,
    };
    
    // فقط برای change-purchase، فیلد status اضافه شود
    if (actionType === 'change-purchase' && status !== null) {
      payload.status = status;
    }
    
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

// Product Categories API
export const productCategoriesApi = {
  getCreateData: async () => {
    const url = getApiUrl('product-categories/create');
    return apiRequest(url, { method: 'GET' });
  },

  create: async (categoryName) => {
    const url = getApiUrl('product-categories');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ category_name: categoryName }),
    });
  },

  update: async (id, categoryName) => {
    const url = getApiUrl(`product-categories/${id}`);
    return apiRequest(url, {
      method: 'PATCH',
      body: JSON.stringify({ category_name: categoryName }),
    });
  },

  delete: async (id) => {
    const url = getApiUrl(`product-categories/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

// Product Subcategories API
export const productSubCategoriesApi = {
  getCreateData: async () => {
    const url = getApiUrl('product-subcategories/create');
    return apiRequest(url, { method: 'GET' });
  },

  getByCategory: async (categoryId) => {
    const url = getApiUrl(`product-subcategories/get/${categoryId}`);
    return apiRequest(url, { method: 'GET' });
  },

  create: async (categoryId, categoryName) => {
    const url = getApiUrl('product-subcategories');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ category_id: categoryId, category_name: categoryName }),
    });
  },

  update: async (id, data) => {
    const url = getApiUrl(`product-subcategories/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id) => {
    const url = getApiUrl(`product-subcategories/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

// Bank Accounts API
export const bankAccountsApi = {
  getAll: async (filters = {}) => {
    const params = {};
    
    if (filters.type) params.type = filters.type;
    if (filters.status) params.status = filters.status;
    if (filters.account_id) params.account_id = filters.account_id;
    if (filters.account_type) params.account_type = filters.account_type;
    if (filters.bank_name) params.bank_name = filters.bank_name;
    if (filters.search) params.search = filters.search;
    
    const url = getApiUrlWithParams('banks', params);
    console.log('📤 Bank Accounts API URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  getCreateData: async () => {
    const url = getApiUrl('banks/create');
    return apiRequest(url, { method: 'GET' });
  },

  getById: async (id) => {
    const url = getApiUrl(`banks/${id}`);
    return apiRequest(url, { method: 'GET' });
  },

  getEditData: async (id) => {
    const url = getApiUrl(`banks/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },

  create: async (payload) => {
    const url = getApiUrl('banks');
    
    // استفاده از FormData برای آپلود فایل
    const formData = new FormData();
    
    formData.append('type', payload.type);
    formData.append('account_name', payload.account_name);
    formData.append('currency_id', payload.currency_id);
    
    if (payload.contact_number) formData.append('contact_number', payload.contact_number);
    if (payload.opening_balance !== undefined) formData.append('opening_balance', payload.opening_balance);
    if (payload.status !== undefined) formData.append('status', payload.status);
    
    // فیلدهای مخصوص نوع bank
    if (payload.type === 'bank') {
      if (payload.bank_name) formData.append('bank_name', payload.bank_name);
      if (payload.account_number) formData.append('account_number', payload.account_number);
      if (payload.account_type) formData.append('account_type', payload.account_type);
      if (payload.bank_logo) formData.append('bank_logo', payload.bank_logo);
    }
    
    return apiRequest(url, {
      method: 'POST',
      body: formData,
    });
  },

  update: async (id, payload) => {
    const url = getApiUrl(`banks/${id}`);
    
    // استفاده از FormData برای آپلود فایل
    const formData = new FormData();
    
    formData.append('type', payload.type);
    formData.append('account_name', payload.account_name);
    formData.append('currency_id', payload.currency_id);
    
    if (payload.contact_number) formData.append('contact_number', payload.contact_number);
    if (payload.opening_balance !== undefined) formData.append('opening_balance', payload.opening_balance);
    if (payload.status !== undefined) formData.append('status', payload.status);
    
    // فیلدهای مخصوص نوع bank
    if (payload.type === 'bank') {
      if (payload.bank_name) formData.append('bank_name', payload.bank_name);
      if (payload.account_number) formData.append('account_number', payload.account_number);
      if (payload.account_type) formData.append('account_type', payload.account_type);
      if (payload.bank_logo) formData.append('bank_logo', payload.bank_logo);
    }
    
    formData.append('_method', 'PUT'); // برای Laravel
    
    return apiRequest(url, {
      method: 'POST',
      body: formData,
    });
  },

  delete: async (id) => {
    const url = getApiUrl(`banks/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  changeStatus: async (accountId, status) => {
    const url = getApiUrl('banks/change-status');
    return apiRequest(url, {
      method: 'PATCH',
      body: JSON.stringify({
        account_id: accountId,
        status: status,
      }),
    });
  },

  // Transaction APIs
  getTransactionCreateData: async (type, accountId) => {
    const url = getApiUrlWithParams('banks/create-transaction', { type, account_id: accountId });
    return apiRequest(url, { method: 'GET' });
  },

  createTransaction: async (payload) => {
    const url = getApiUrl('banks/store-transaction');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getTransaction: async (id) => {
    const url = getApiUrl(`banks/view-transaction/${id}`);
    return apiRequest(url, { method: 'GET' });
  },

  deleteTransaction: async (id) => {
    const url = getApiUrl(`banks/delete-transaction/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  // عملیات گروهی روی حساب‌های بانکی
  applyQuickAction: async (actionType, rowIds) => {
    const url = getApiUrl('banks/apply-quick-action');
    const payload = {
      action_type: actionType,
      row_ids: Array.isArray(rowIds) ? rowIds.join(',') : rowIds,
    };
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  exportList: async (filters = {}) => {
    const params = {
      type: filters.type || 'all',
      status: filters.status || 'all',
      account_id: filters.account_id || 'all',
      account_type: filters.account_type || 'all',
      bank_name: filters.bank_name || 'all',
    };
    if (filters.search !== undefined) {
      params.search = filters.search;
    }
    const url = getApiUrlWithParams('banks/export', params);
    return downloadExportFile(url, { filenameFallback: 'bank-accounts.xlsx' });
  },
};

// Expenses API
export const expensesApi = {
  getAll: async (filters = {}) => {
    const params = {};
    
    if (filters.startDate) params.startDate = formatJalaliApiDate(filters.startDate);
    if (filters.endDate) params.endDate = formatJalaliApiDate(filters.endDate);
    if (filters.status) params.status = filters.status;
    if (filters.employee_id) params.employee_id = filters.employee_id;
    if (filters.project_id) params.project_id = filters.project_id;
    if (filters.category_id) params.category_id = filters.category_id;
    if (filters.search) params.search = filters.search;
    if (filters.page) params.page = filters.page;
    if (filters.per_page) params.per_page = filters.per_page;
    
    const url = getApiUrlWithParams('expenses', params);
    console.log('📤 Expenses API URL:', url);
    return apiRequest(url, { method: 'GET' });
  },

  getCreateData: async (params = {}) => {
    const query = {};
    if (params.project_id != null && params.project_id !== '') query.project_id = params.project_id;
    const url =
      Object.keys(query).length > 0
        ? getApiUrlWithParams('expenses/create', query)
        : getApiUrl('expenses/create');
    return apiRequest(url, { method: 'GET' });
  },

  getById: async (id) => {
    const url = getApiUrl(`expenses/${id}`);
    return apiRequest(url, { method: 'GET' });
  },

  create: async (payload) => {
    const url = getApiUrl('expenses');
    
    const formData = new FormData();
    
    formData.append('item_name', payload.item_name);
    formData.append('currency_id', payload.currency_id);
    if (payload.exchange_rate) formData.append('exchange_rate', payload.exchange_rate);
    formData.append('price', payload.price);
    if (payload.purchase_date) formData.append('purchase_date', formatJalaliApiDate(payload.purchase_date));
    if (payload.user_id) formData.append('user_id', payload.user_id);
    if (payload.project_id) formData.append('project_id', payload.project_id);
    if (payload.category_id) formData.append('category_id', payload.category_id);
    if (payload.purchase_from) formData.append('purchase_from', payload.purchase_from);
    if (payload.bank_account_id) formData.append('bank_account_id', payload.bank_account_id);
    if (payload.description) formData.append('description', payload.description);
    if (payload.bill) formData.append('bill', payload.bill);
    
    return apiRequest(url, {
      method: 'POST',
      body: formData,
    });
  },

  update: async (id, payload) => {
    const url = getApiUrl(`expenses/${id}`);
    
    const formData = new FormData();
    
    formData.append('item_name', payload.item_name);
    formData.append('currency_id', payload.currency_id);
    if (payload.exchange_rate) formData.append('exchange_rate', payload.exchange_rate);
    formData.append('price', payload.price);
    if (payload.purchase_date) formData.append('purchase_date', formatJalaliApiDate(payload.purchase_date));
    if (payload.user_id) formData.append('user_id', payload.user_id);
    if (payload.project_id) formData.append('project_id', payload.project_id);
    if (payload.category_id) formData.append('category_id', payload.category_id);
    if (payload.purchase_from) formData.append('purchase_from', payload.purchase_from);
    if (payload.bank_account_id) formData.append('bank_account_id', payload.bank_account_id);
    if (payload.description) formData.append('description', payload.description);
    if (payload.bill) formData.append('bill', payload.bill);
    formData.append('_method', 'PUT');
    
    return apiRequest(url, {
      method: 'POST',
      body: formData,
    });
  },

  delete: async (id) => {
    const url = getApiUrl(`expenses/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  pay: async (id, payload) => {
    const url = getApiUrl(`financial/expenses/${id}/pay`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateStatus: async (expenseId, status) => {
    const url = getApiUrl('expenses/update-status');
    return apiRequest(url, {
      method: 'PATCH',
      body: JSON.stringify({
        expense_id: expenseId,
        status: status,
      }),
    });
  },

  // عملیات گروهی روی هزینه‌ها
  applyQuickAction: async (actionType, rowIds, status = null) => {
    const url = getApiUrl('expenses/apply-quick-action');
    const payload = {
      action_type: actionType,
      row_ids: Array.isArray(rowIds) ? rowIds.join(',') : rowIds,
    };
    if (actionType === 'change-status' && status) {
      payload.status = status;
    }
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

// Expense Categories API
export const expenseCategoriesApi = {
  getCreateData: async () => {
    const url = getApiUrl('expense-categories/create');
    return apiRequest(url, { method: 'GET' });
  },

  create: async (categoryName, roles = []) => {
    const url = getApiUrl('expense-categories/store');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({
        category_name: categoryName,
        role: roles,
      }),
    });
  },

  update: async (id, data) => {
    const url = getApiUrl(`expense-categories/update/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id) => {
    const url = getApiUrl(`expense-categories/destroy/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

// Recurring Expenses API
export const recurringExpensesApi = {
  getAll: async (filters = {}) => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.project_id) params.project_id = filters.project_id;
    if (filters.user_id) params.user_id = filters.user_id;
    if (filters.category_id) params.category_id = filters.category_id;
    if (filters.search) params.search = filters.search;
    if (filters.page) params.page = filters.page;
    if (filters.per_page) params.per_page = filters.per_page;

    const url = getApiUrlWithParams('recurring-expenses', params);
    return apiRequest(url, { method: 'GET' });
  },

  getCreateData: async () => {
    const url = getApiUrl('recurring-expenses/create');
    return apiRequest(url, { method: 'GET' });
  },

  getById: async (id) => {
    const url = getApiUrl(`recurring-expenses/${id}`);
    return apiRequest(url, { method: 'GET' });
  },

  getEditData: async (id) => {
    const url = getApiUrl(`recurring-expenses/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },

  create: async (payload) => {
    const url = getApiUrl('recurring-expenses');
    const formData = new FormData();

    formData.append('item_name', payload.item_name);
    formData.append('price', payload.price);
    formData.append('currency_id', payload.currency_id);
    if (payload.category_id) formData.append('category_id', payload.category_id);
    if (payload.user_id) formData.append('user_id', payload.user_id);
    formData.append('status', payload.status || 'active');
    formData.append('rotation', payload.rotation);
    if (payload.billing_cycle) formData.append('billing_cycle', payload.billing_cycle);
    if (payload.description) formData.append('description', payload.description);
    if (payload.purchase_from) formData.append('purchase_from', payload.purchase_from);
    if (payload.issue_date) formData.append('issue_date', formatJalaliApiDate(payload.issue_date));
    if (payload.project_id) formData.append('project_id', payload.project_id);
    if (payload.bank_account_id) formData.append('bank_account_id', payload.bank_account_id);
    if (payload.bill) formData.append('bill', payload.bill);
    if (payload.immediate_expense !== undefined) formData.append('immediate_expense', payload.immediate_expense ? 1 : 0);

    return apiRequest(url, {
      method: 'POST',
      body: formData,
    });
  },

  update: async (id, payload) => {
    const url = getApiUrl(`recurring-expenses/${id}`);
    const formData = new FormData();

    if (payload.expense_count !== undefined) formData.append('expense_count', payload.expense_count);
    formData.append('item_name', payload.item_name);
    formData.append('price', payload.price);
    formData.append('currency_id', payload.currency_id);
    if (payload.category_id) formData.append('category_id', payload.category_id);
    if (payload.user_id) formData.append('user_id', payload.user_id);
    formData.append('rotation', payload.rotation);
    if (payload.billing_cycle) formData.append('billing_cycle', payload.billing_cycle);
    if (payload.description) formData.append('description', payload.description);
    if (payload.purchase_from) formData.append('purchase_from', payload.purchase_from);
    if (payload.bank_account_id) formData.append('bank_account_id', payload.bank_account_id);
    if (payload.issue_date) formData.append('issue_date', formatJalaliApiDate(payload.issue_date));
    if (payload.project_id) formData.append('project_id', payload.project_id);
    if (payload.status) formData.append('status', payload.status);
    if (payload.bill) formData.append('bill', payload.bill);
    formData.append('_method', 'PUT');

    return apiRequest(url, {
      method: 'POST',
      body: formData,
    });
  },

  delete: async (id) => {
    const url = getApiUrl(`recurring-expenses/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  getAlerts: async () => {
    const url = getApiUrl('financial/recurring-expenses/alerts');
    return apiRequest(url, { method: 'GET' });
  },

  updateStatus: async (expenseId, status) => {
    const url = getApiUrl('recurring-expenses/update-status');
    return apiRequest(url, {
      method: 'PATCH',
      body: JSON.stringify({
        expense_id: expenseId,
        status: status,
      }),
    });
  },
};

// Orders API
export const ordersApi = {
  getAll: async (filters = {}) => {
    const params = {};
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.client_id) params.client_id = filters.client_id;
    if (filters.project_id) params.project_id = filters.project_id;
    if (filters.status) params.status = filters.status;
    if (filters.search) params.search = filters.search;
    if (filters.page) params.page = filters.page;
    if (filters.per_page) params.per_page = filters.per_page;
    const url = getApiUrlWithParams('orders', params);
    return apiRequest(url, { method: 'GET' });
  },
  getCreateData: async (params = {}) => {
    const query = {};
    if (params.project_id != null && params.project_id !== '') query.project_id = params.project_id;
    if (params.client_id != null && params.client_id !== '') query.client_id = params.client_id;
    const url =
      Object.keys(query).length > 0
        ? getApiUrlWithParams('orders/create', query)
        : getApiUrl('orders/create');
    return apiRequest(url, { method: 'GET' });
  },
  getById: async (id) => {
    const url = getApiUrl(`orders/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('orders');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    const url = getApiUrl(`orders/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`orders/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  changeStatus: async (data) => {
    const url = getApiUrl('orders/change-status');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// تیکت‌ها
export const ticketsApi = {
  getAll: async (filters = {}) => {
    const params = {};
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.status != null && filters.status !== '') params.status = filters.status;
    if (filters.group_id != null && filters.group_id !== '') params.group_id = filters.group_id;
    if (filters.agent_id != null && filters.agent_id !== '') params.agent_id = filters.agent_id;
    if (filters.priority != null && filters.priority !== '') params.priority = filters.priority;
    if (filters.channel_id != null && filters.channel_id !== '') params.channel_id = filters.channel_id;
    if (filters.type_id != null && filters.type_id !== '') params.type_id = filters.type_id;
    if (filters.tag_id != null && filters.tag_id !== '') params.tag_id = filters.tag_id;
    if (filters.project_id != null && filters.project_id !== '') params.project_id = filters.project_id;
    if (filters.search !== undefined && filters.search !== null) params.search = filters.search;
    if (filters.page) params.page = filters.page;
    if (filters.per_page) params.per_page = filters.per_page;
    const url = getApiUrlWithParams('tickets', params);
    return apiRequest(url, { method: 'GET' });
  },
  refreshCount: async (filters = {}) => {
    const params = {};
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.tag_id != null && filters.tag_id !== '') params.tag_id = filters.tag_id;
    if (filters.agent_id != null && filters.agent_id !== '') params.agent_id = filters.agent_id;
    if (filters.priority != null && filters.priority !== '') params.priority = filters.priority;
    if (filters.channel_id != null && filters.channel_id !== '') params.channel_id = filters.channel_id;
    if (filters.type_id != null && filters.type_id !== '') params.type_id = filters.type_id;
    if (filters.group_id != null && filters.group_id !== '') params.group_id = filters.group_id;
    if (filters.project_id != null && filters.project_id !== '') params.project_id = filters.project_id;
    const url = getApiUrlWithParams('tickets/refresh-count', params);
    return apiRequest(url, { method: 'GET' });
  },
  getCreateData: async (params = {}) => {
    const query = {};
    if (params.project != null && params.project !== '') query.project = params.project;
    if (params.project_id != null && params.project_id !== '') query.project = params.project_id;
    const url =
      Object.keys(query).length > 0
        ? getApiUrlWithParams('tickets/create', query)
        : getApiUrl('tickets/create');
    return apiRequest(url, { method: 'GET' });
  },
  getById: async (id) => {
    const url = getApiUrl(`tickets/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('tickets');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  changeStatus: async (ticketId, status) => {
    const url = getApiUrl('tickets/change-status');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ ticket_id: ticketId, status }),
    });
  },
  applyQuickAction: async (actionType, rowIds, status = null) => {
    const url = getApiUrl('tickets/apply-quick-action');
    const payload = {
      action_type: actionType,
      row_ids: Array.isArray(rowIds) ? rowIds.join(',') : rowIds,
    };
    if (actionType === 'change-status' && status) {
      payload.status = status;
    }
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getEditDetails: async (id) => {
    const url = getApiUrl(`tickets/edit-details/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  updateDetails: async (id, data) => {
    const url = getApiUrl(`tickets/update-details/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  updateOtherData: async (id, data) => {
    const url = getApiUrl(`tickets/update-other-data/${id}`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  /** پاسخ یا یادداشت جدید — PUT tickets/:id */
  addReplyOrNote: async (id, data) => {
    const url = getApiUrl(`tickets/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteReply: async (replyId) => {
    const url = getApiUrl(`ticket-replies/${replyId}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  editReplyNote: async (payload) => {
    const url = getApiUrl('ticket-replies/edit-note');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getCustomerTicketForm: async () => {
    const url = getApiUrl('ticket-form');
    return apiRequest(url, { method: 'GET' });
  },
  updateCustomerTicketFormField: async (fieldId, status) => {
    const url = getApiUrl(`ticket-form/${fieldId}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },
  sortCustomerTicketFormFields: async (sortedValues) => {
    const url = getApiUrl('ticket-form/sort-fields');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ sorted_values: sortedValues }),
    });
  },
};

/** تنظیمات مرکز تماس — callcenter/settings */
export const callCenterSettingsApi = {
  getSettings: async () => {
    const url = getApiUrl('callcenter/settings');
    return apiRequest(url, { method: 'GET' });
  },
  updateSettings: async (payload) => {
    const url = getApiUrl('callcenter/settings');
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  testConnection: async () => {
    const url = getApiUrl('callcenter/settings/test-connection');
    return apiRequest(url, { method: 'POST' });
  },
  clearCache: async () => {
    const url = getApiUrl('callcenter/settings/clear-cache');
    return apiRequest(url, { method: 'POST' });
  },
  testGapGpt: async (payload = {}) => {
    const url = getApiUrl('callcenter/settings/test-gapgpt');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

/** اپراتورهای مرکز تماس — callcenter/agents */
export const callCenterAgentsApi = {
  getAll: async () => {
    const url = getApiUrl('callcenter/agents');
    return apiRequest(url, { method: 'GET' });
  },
  getCreateForm: async () => {
    const url = getApiUrl('callcenter/agents/create');
    return apiRequest(url, { method: 'GET' });
  },
  create: async ({ user_id, extension, queue_id, queue, is_active, simotel_user_id, simotel_number }) => {
    const url = getApiUrl('callcenter/agents');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({
        user_id: Number(user_id),
        extension: String(extension),
        queue_id: queue_id != null ? Number(queue_id) : null,
        queue: queue || null,
        is_active: is_active !== false,
        simotel_user_id: simotel_user_id || null,
        simotel_number: simotel_number || null,
      }),
    });
  },
  update: async (id, { user_id, extension, queue_id, queue, is_active, simotel_user_id, simotel_number }) => {
    const url = getApiUrl(`callcenter/agents/${id}`);
    const body = {};
    if (user_id != null) body.user_id = Number(user_id);
    if (extension != null) body.extension = String(extension);
    if (queue_id !== undefined) body.queue_id = queue_id != null ? Number(queue_id) : null;
    if (queue !== undefined) body.queue = queue;
    if (is_active !== undefined) body.is_active = is_active !== false;
    if (simotel_user_id !== undefined) body.simotel_user_id = simotel_user_id;
    if (simotel_number !== undefined) body.simotel_number = simotel_number;
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  updateStatus: async ({ status, agent_extension_id }) => {
    const url = getApiUrl('callcenter/agents/status');
    const body = { status };
    if (agent_extension_id != null) {
      body.agent_extension_id = agent_extension_id;
    }
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  remove: async (id) => {
    const url = getApiUrl(`callcenter/agents/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** گفتگوی آنلاین — livechat/* */
export const liveChatApi = {
  getSettings: async () => apiRequest(getApiUrl('livechat/settings'), { method: 'GET' }),
  updateSettings: async (payload) =>
    apiRequest(getApiUrl('livechat/settings'), { method: 'PUT', body: JSON.stringify(payload) }),
  regenerateWidgetKey: async () => apiRequest(getApiUrl('livechat/settings/widget-key'), { method: 'POST' }),
  generateWidgetApiKey: async () => apiRequest(getApiUrl('livechat/settings/api-key'), { method: 'POST' }),
  revokeWidgetApiKey: async () => apiRequest(getApiUrl('livechat/settings/api-key'), { method: 'DELETE' }),
  testBotProxy: async (botProxy) =>
    apiRequest(getApiUrl('livechat/settings/bot-proxy/test'), { method: 'POST', body: JSON.stringify({ bot_proxy: botProxy || null }) }),
  uploadOperatorAvatar: async (file) => {
    const form = new FormData();
    form.append('image', file, file.name);
    return apiRequest(getApiUrl('livechat/settings/operator-avatar'), { method: 'POST', body: form });
  },
  removeOperatorAvatar: async () => apiRequest(getApiUrl('livechat/settings/operator-avatar'), { method: 'DELETE' }),

  getDashboard: async (params = {}) =>
    apiRequest(getApiUrlWithParams('livechat/dashboard', params), { method: 'GET' }),

  getChannels: async () => apiRequest(getApiUrl('livechat/channels'), { method: 'GET' }),

  getDepartments: async () => apiRequest(getApiUrl('livechat/departments'), { method: 'GET' }),
  createDepartment: async (payload) =>
    apiRequest(getApiUrl('livechat/departments'), { method: 'POST', body: JSON.stringify(payload) }),
  updateDepartment: async (id, payload) =>
    apiRequest(getApiUrl(`livechat/departments/${id}`), { method: 'PUT', body: JSON.stringify(payload) }),
  removeDepartment: async (id) => apiRequest(getApiUrl(`livechat/departments/${id}`), { method: 'DELETE' }),
  getSubDepartments: async (params = {}) => apiRequest(getApiUrlWithParams('livechat/sub-departments', params), { method: 'GET' }),
  createSubDepartment: async (payload) =>
    apiRequest(getApiUrl('livechat/sub-departments'), { method: 'POST', body: JSON.stringify(payload) }),
  updateSubDepartment: async (id, payload) =>
    apiRequest(getApiUrl(`livechat/sub-departments/${id}`), { method: 'PUT', body: JSON.stringify(payload) }),
  removeSubDepartment: async (id) => apiRequest(getApiUrl(`livechat/sub-departments/${id}`), { method: 'DELETE' }),
  // پاسخ خودکار (کامنت اینستاگرام)
  getAutoReplies: async () => apiRequest(getApiUrl('livechat/auto-replies'), { method: 'GET' }),
  getAutoReplyPosts: async (channelId) =>
    apiRequest(getApiUrlWithParams('livechat/auto-replies/posts', { channel_id: channelId }), { method: 'GET' }),
  createAutoReply: async (payload) =>
    apiRequest(getApiUrl('livechat/auto-replies'), { method: 'POST', body: JSON.stringify(payload) }),
  updateAutoReply: async (id, payload) =>
    apiRequest(getApiUrl(`livechat/auto-replies/${id}`), { method: 'PUT', body: JSON.stringify(payload) }),
  removeAutoReply: async (id) => apiRequest(getApiUrl(`livechat/auto-replies/${id}`), { method: 'DELETE' }),
  setConversationSubDepartment: async (id, subDepartmentId) =>
    apiRequest(getApiUrl(`livechat/conversations/${id}/sub-department`), {
      method: 'POST',
      body: JSON.stringify({ sub_department_id: subDepartmentId ?? null }),
    }),
  setConversationDepartment: async (id, departmentId) =>
    apiRequest(getApiUrl(`livechat/conversations/${id}/department`), {
      method: 'POST',
      body: JSON.stringify({ department_id: departmentId ?? null }),
    }),

  // ربات‌های پیام‌رسان؛ platform: 'bale' | 'telegram'
  getBots: async (platform) => apiRequest(getApiUrl(`livechat/${platform}-bots`), { method: 'GET' }),
  createBot: async (platform, payload) =>
    apiRequest(getApiUrl(`livechat/${platform}-bots`), { method: 'POST', body: JSON.stringify(payload) }),
  updateBot: async (platform, id, payload) =>
    apiRequest(getApiUrl(`livechat/${platform}-bots/${id}`), { method: 'PUT', body: JSON.stringify(payload) }),
  removeBot: async (platform, id) => apiRequest(getApiUrl(`livechat/${platform}-bots/${id}`), { method: 'DELETE' }),
  checkBot: async (platform, id) => apiRequest(getApiUrl(`livechat/${platform}-bots/${id}/check`), { method: 'POST' }),
  // حساب‌های اینستاگرام (دایرکت)
  getInstagramAccounts: async () => apiRequest(getApiUrl('livechat/instagram-accounts'), { method: 'GET' }),
  createInstagramAccount: async (payload) =>
    apiRequest(getApiUrl('livechat/instagram-accounts'), { method: 'POST', body: JSON.stringify(payload) }),
  updateInstagramAccount: async (id, payload) =>
    apiRequest(getApiUrl(`livechat/instagram-accounts/${id}`), { method: 'PUT', body: JSON.stringify(payload) }),
  removeInstagramAccount: async (id) => apiRequest(getApiUrl(`livechat/instagram-accounts/${id}`), { method: 'DELETE' }),
  checkInstagramAccount: async (id) => apiRequest(getApiUrl(`livechat/instagram-accounts/${id}/check`), { method: 'POST' }),
  // اینستاگرام از طریق Zernio
  getZernioInstagramAccounts: async () => apiRequest(getApiUrl('livechat/instagram-zernio'), { method: 'GET' }),
  zernioLookup: async (payload) =>
    apiRequest(getApiUrl('livechat/instagram-zernio/lookup'), { method: 'POST', body: JSON.stringify(payload) }),
  zernioConnectUrl: async (payload) =>
    apiRequest(getApiUrl('livechat/instagram-zernio/connect-url'), { method: 'POST', body: JSON.stringify(payload) }),
  createZernioInstagramAccount: async (payload) =>
    apiRequest(getApiUrl('livechat/instagram-zernio'), { method: 'POST', body: JSON.stringify(payload) }),
  updateZernioInstagramAccount: async (id, payload) =>
    apiRequest(getApiUrl(`livechat/instagram-zernio/${id}`), { method: 'PUT', body: JSON.stringify(payload) }),
  removeZernioInstagramAccount: async (id) => apiRequest(getApiUrl(`livechat/instagram-zernio/${id}`), { method: 'DELETE' }),
  checkZernioInstagramAccount: async (id) => apiRequest(getApiUrl(`livechat/instagram-zernio/${id}/check`), { method: 'POST' }),
  createChannel: async (payload) =>
    apiRequest(getApiUrl('livechat/channels'), { method: 'POST', body: JSON.stringify(payload) }),
  updateChannel: async (id, payload) =>
    apiRequest(getApiUrl(`livechat/channels/${id}`), { method: 'PUT', body: JSON.stringify(payload) }),
  removeChannel: async (id) => apiRequest(getApiUrl(`livechat/channels/${id}`), { method: 'DELETE' }),

  getOperators: async () => apiRequest(getApiUrl('livechat/operators'), { method: 'GET' }),
  getOperatorForm: async () => apiRequest(getApiUrl('livechat/operators/create'), { method: 'GET' }),
  createOperator: async (payload) =>
    apiRequest(getApiUrl('livechat/operators'), { method: 'POST', body: JSON.stringify(payload) }),
  updateOperator: async (id, payload) =>
    apiRequest(getApiUrl(`livechat/operators/${id}`), { method: 'PUT', body: JSON.stringify(payload) }),
  removeOperator: async (id) => apiRequest(getApiUrl(`livechat/operators/${id}`), { method: 'DELETE' }),
  getMe: async () => apiRequest(getApiUrl('livechat/operators/me'), { method: 'GET' }),
  updateMyStatus: async (status) =>
    apiRequest(getApiUrl('livechat/operators/me/status'), { method: 'POST', body: JSON.stringify({ status }) }),

  getConversations: async (filters = {}) =>
    apiRequest(getApiUrlWithParams('livechat/conversations', filters), { method: 'GET' }),
  getConversation: async (id) => apiRequest(getApiUrl(`livechat/conversations/${id}`), { method: 'GET' }),
  getMessages: async (id, params = {}) =>
    apiRequest(getApiUrlWithParams(`livechat/conversations/${id}/messages`, params), { method: 'GET' }),
  /** پاسخ اپراتور؛ با فایل به‌صورت multipart ارسال می‌شود */
  reply: async (id, body, files = [], durations = []) => {
    const url = getApiUrl(`livechat/conversations/${id}/messages`);
    if (!files.length) {
      return apiRequest(url, { method: 'POST', body: JSON.stringify({ body }) });
    }
    const form = new FormData();
    if (body) form.append('body', body);
    files.forEach((file, i) => {
      form.append('files[]', file, file.name);
      if (durations[i] != null) form.append(`durations[${i}]`, String(durations[i]));
    });
    return apiRequest(url, { method: 'POST', body: form });
  },
  markRead: async (id) => apiRequest(getApiUrl(`livechat/conversations/${id}/read`), { method: 'POST' }),
  // پیام‌های قبلی مخاطب در دایرکت اینستاگرام
  importInstagramHistory: async (id) =>
    apiRequest(getApiUrl(`livechat/conversations/${id}/instagram-history`), { method: 'POST' }),
  assign: async (id, operatorId) =>
    apiRequest(getApiUrl(`livechat/conversations/${id}/assign`), {
      method: 'POST',
      body: JSON.stringify({ operator_id: operatorId ?? null }),
    }),
  getEvents: async (id) => apiRequest(getApiUrl(`livechat/conversations/${id}/events`), { method: 'GET' }),
  getTransferTargets: async (id) =>
    apiRequest(getApiUrl(`livechat/conversations/${id}/transfer-targets`), { method: 'GET' }),
  /** subDepartmentId: undefined = بدون تغییر زیربخش، null = حذف زیربخش */
  transfer: async (id, operatorId, note, subDepartmentId) =>
    apiRequest(getApiUrl(`livechat/conversations/${id}/transfer`), {
      method: 'POST',
      body: JSON.stringify({
        operator_id: operatorId,
        note: note || null,
        ...(subDepartmentId !== undefined ? { sub_department_id: subDepartmentId } : {}),
      }),
    }),
  close: async (id) => apiRequest(getApiUrl(`livechat/conversations/${id}/close`), { method: 'POST' }),
  reopen: async (id) => apiRequest(getApiUrl(`livechat/conversations/${id}/reopen`), { method: 'POST' }),

  getTestOptions: async () => apiRequest(getApiUrl('livechat/test/options'), { method: 'GET' }),
  sendTestMessage: async (payload) =>
    apiRequest(getApiUrl('livechat/test/messages'), { method: 'POST', body: JSON.stringify(payload) }),
  getTestConversation: async (id, params = {}) =>
    apiRequest(getApiUrlWithParams(`livechat/test/conversations/${id}`, params), { method: 'GET' }),
};

/** تماس‌های مرکز تماس — callcenter/calls */
export const callCenterCallsApi = {
  sync: async ({ sync: doSync = true, from, to, extensions } = {}) => {
    const url = getApiUrl('callcenter/calls/sync');
    const payload = { sync: doSync };
    if (from) payload.from = from;
    if (to) payload.to = to;
    if (extensions?.length) payload.extensions = extensions;
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getAll: async ({ page = 1, per_page = 20, ...filters } = {}) => {
    const url = getApiUrlWithParams('callcenter/calls', { page, per_page, ...filters });
    return apiRequest(url, { method: 'GET' });
  },
  getById: async (id) => {
    const url = getApiUrl(`callcenter/calls/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  getAudioLink: async (callId) => {
    const url = getApiUrl('callcenter/calls/audio');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ call_id: Number(callId) }),
    });
  },
  transcribe: async (callId) => {
    const url = getApiUrl(`callcenter/calls/${callId}/transcribe`);
    return apiRequest(url, { method: 'POST' });
  },
  getTranscribeLogs: async (callId) => {
    const url = getApiUrl(`callcenter/calls/${callId}/transcribe-logs`);
    return apiRequest(url, { method: 'GET' });
  },
  clearTranscribeLogs: async (callId) => {
    const url = getApiUrl(`callcenter/calls/${callId}/transcribe-logs`);
    return apiRequest(url, { method: 'DELETE' });
  },
  getAllTranscribeLogs: async ({ page = 1, per_page = 20, ...filters } = {}) => {
    const url = getApiUrlWithParams('callcenter/transcribe-logs', { page, per_page, ...filters });
    return apiRequest(url, { method: 'GET' });
  },
  summarize: async (id) => {
    const url = getApiUrl(`callcenter/calls/${id}/summarize`);
    return apiRequest(url, { method: 'POST' });
  },
  /** خلاصه‌سازی گروهی: { call_ids?, mode: missing|failed|all, ...فیلترهای لیست تماس } */
  summarizeBulk: async (payload = {}) => {
    const url = getApiUrl('callcenter/calls/ai-summary/bulk');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getSummaryStats: async (filters = {}) => {
    const url = getApiUrlWithParams('callcenter/calls/ai-summary/stats', filters);
    return apiRequest(url, { method: 'GET' });
  },
  /** وضعیت سبک رونویسی/خلاصه برای polling (حداکثر ۱۰۰ شناسه) */
  getProcessingStatus: async (ids = []) => {
    const url = getApiUrlWithParams('callcenter/calls/processing-status', { 'ids[]': ids });
    return apiRequest(url, { method: 'GET' });
  },
  update: async (id, data) => {
    const url = getApiUrl(`callcenter/calls/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

/** Screen Pop و Click-to-Call */
export const callCenterPopApi = {
  getScreenPop: async ({ phone, extension } = {}) => {
    const params = {};
    if (phone) params.phone = phone;
    if (extension) params.extension = extension;
    const url = getApiUrlWithParams('callcenter/screen-pop', params);
    return apiRequest(url, { method: 'GET' });
  },
  clickToCall: async ({ destination, phone, extension, lead_id, deal_id, client_id } = {}) => {
    const url = getApiUrl('callcenter/click-to-call');
    const target = destination || phone;
    const body = {};
    if (target) {
      body.destination = target;
      body.phone = target;
    }
    if (extension) body.extension = String(extension);
    if (lead_id != null) body.lead_id = lead_id;
    if (deal_id != null) body.deal_id = deal_id;
    if (client_id != null) body.client_id = client_id;
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
};

/** گزارش‌های مرکز تماس — callcenter/reports */
export const callCenterReportsApi = {
  getDashboard: async ({ from, to, refresh } = {}) => {
    const url = getApiUrlWithParams('callcenter/dashboard', { from, to, ...(refresh ? { refresh: 1 } : {}) });
    return apiRequest(url, { method: 'GET' });
  },
  getHourlyHeatmap: async ({ from, to } = {}) => {
    const url = getApiUrlWithParams('callcenter/dashboard/hourly', { from, to });
    return apiRequest(url, { method: 'GET' });
  },
  getRepeatCallers: async ({ from, to, minCalls, limit } = {}) => {
    const url = getApiUrlWithParams('callcenter/dashboard/repeat-callers', {
      from,
      to,
      ...(minCalls ? { min_calls: minCalls } : {}),
      ...(limit ? { limit } : {}),
    });
    return apiRequest(url, { method: 'GET' });
  },
  getAgentPerformance: async ({ from, to }) => {
    const url = getApiUrlWithParams('callcenter/reports/agents', { from, to });
    return apiRequest(url, { method: 'GET' });
  },
  getQualityComparison: async ({ from, to }) => {
    const url = getApiUrlWithParams('callcenter/reports/quality-comparison', { from, to });
    return apiRequest(url, { method: 'GET' });
  },
};

/** لیست سیاه مرکز تماس — callcenter/blacklist */
export const callCenterBlacklistApi = {
  getAll: async ({ page = 1, per_page = 20 } = {}) => {
    const url = getApiUrlWithParams('callcenter/blacklist', { page, per_page });
    return apiRequest(url, { method: 'GET' });
  },
  add: async ({ phone, reason }) => {
    const url = getApiUrl('callcenter/blacklist');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({
        phone: String(phone).trim(),
        reason: reason?.trim() || null,
      }),
    });
  },
  remove: async (id) => {
    const url = getApiUrl(`callcenter/blacklist/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** صف‌های مرکز تماس — callcenter/queues */
export const callCenterQueuesApi = {
  getAll: async () => {
    const url = getApiUrl('callcenter/queues');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('callcenter/queues');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    const url = getApiUrl(`callcenter/queues/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`callcenter/queues/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** موضوعات تماس (برچسب‌های رنگی) — callcenter/subjects */
export const callCenterSubjectsApi = {
  getAll: async () => {
    const url = getApiUrl('callcenter/subjects');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('callcenter/subjects');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    const url = getApiUrl(`callcenter/subjects/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`callcenter/subjects/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** اعتراض‌های مشتری (فهرست تعریف‌شده برای تشخیص AI) — callcenter/objections */
export const callCenterObjectionsApi = {
  getAll: async () => {
    const url = getApiUrl('callcenter/objections');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('callcenter/objections');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    const url = getApiUrl(`callcenter/objections/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`callcenter/objections/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** استانداردهای ارزیابی اپراتور به تفکیک صف — callcenter/standards */
export const callCenterStandardsApi = {
  getAll: async ({ queue_id } = {}) => {
    const url = getApiUrlWithParams('callcenter/standards', { queue_id });
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('callcenter/standards');
    return apiRequest(url, { method: 'POST', body: JSON.stringify(data) });
  },
  update: async (id, data) => {
    const url = getApiUrl(`callcenter/standards/${id}`);
    return apiRequest(url, { method: 'PUT', body: JSON.stringify(data) });
  },
  delete: async (id) => {
    const url = getApiUrl(`callcenter/standards/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** نتایج تماس — callcenter/outcomes */
export const callCenterOutcomesApi = {
  getAll: async ({ queue } = {}) => {
    const url = getApiUrlWithParams('callcenter/outcomes', { queue });
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('callcenter/outcomes');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    const url = getApiUrl(`callcenter/outcomes/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`callcenter/outcomes/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** دسته‌بندی‌های پایگاه دانش سازمانی — knowledge-base/categories */
export const knowledgeCategoriesApi = {
  getAll: async (params = {}) => {
    const url = getApiUrlWithParams('knowledge-base/categories', params);
    return apiRequest(url, { method: 'GET' });
  },
  get: async (id) => {
    const url = getApiUrl(`knowledge-base/categories/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('knowledge-base/categories');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    const url = getApiUrl(`knowledge-base/categories/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`knowledge-base/categories/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** پایگاه دانش و پارتیشن‌های چندبخشی سازمانی — knowledge-base/knowledges */
export const knowledgeBaseApi = {
  getAll: async (params = {}) => {
    const url = getApiUrlWithParams('knowledge-base/knowledges', params);
    return apiRequest(url, { method: 'GET' });
  },
  get: async (id) => {
    const url = getApiUrl(`knowledge-base/knowledges/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('knowledge-base/knowledges');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    const url = getApiUrl(`knowledge-base/knowledges/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`knowledge-base/knowledges/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  getPromptPreview: async (params = {}) => {
    const url = getApiUrlWithParams('knowledge-base/knowledges/prompt-preview', params);
    return apiRequest(url, { method: 'GET' });
  },
};

/** RAG پایگاه دانش: تنظیمات GapGPT، ایندکس برداری Qdrant و چت آزمایشی — knowledge-base/settings, knowledge-base/rag */
export const knowledgeRagApi = {
  getSettings: async () => {
    const url = getApiUrl('knowledge-base/settings');
    return apiRequest(url, { method: 'GET' });
  },
  updateSettings: async (payload) => {
    const url = getApiUrl('knowledge-base/settings');
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  testConnection: async (payload = {}) => {
    const url = getApiUrl('knowledge-base/settings/test-connection');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getStatus: async () => {
    const url = getApiUrl('knowledge-base/rag/status');
    return apiRequest(url, { method: 'GET' });
  },
  /** اجرای دستی Embedding: همه پارت‌ها در یک درخواست GapGPT */
  sync: async () => {
    const url = getApiUrl('knowledge-base/rag/sync');
    return apiRequest(url, { method: 'POST' });
  },
  search: async (query, limit = null) => {
    const url = getApiUrl('knowledge-base/rag/search');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(limit ? { query, limit } : { query }),
    });
  },
  chat: async (message, history = []) => {
    const url = getApiUrl('knowledge-base/rag/chat');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ message, history }),
    });
  },
};

/** نام‌های جایگزین برای سازگاری به عقب */
export const callCenterKnowledgeCategoriesApi = knowledgeCategoriesApi;
export const callCenterKnowledgesApi = knowledgeBaseApi;

/** مرکز تماس زنده — callcenter/live */
export const callCenterLiveApi = {
  getLiveStatus: async () => {
    const url = getApiUrl('callcenter/live');
    return apiRequest(url, { method: 'GET' });
  },
};

/** شبیه‌ساز رویدادهای ساختگی تماس مرکز تماس (Fake Call Simulator) — callcenter/fake-call */
export const callCenterFakeCallApi = {
  getOptions: async () => {
    const url = getApiUrl('callcenter/fake-call/options');
    return apiRequest(url, { method: 'GET' });
  },
  publish: async (payload) => {
    const url = getApiUrl('callcenter/fake-call');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

/** تنظیمات تیکت — ticket-settings */
export const ticketSettingsApi = {
  getByTab: async (tab) => {
    const url = getApiUrlWithParams('ticket-settings', { tab });
    return apiRequest(url, { method: 'GET' });
  },
  /** POST ticket-settings/update-status/{companyId} */
  updateRoundRobinStatus: async (companyId, payload) => {
    const url = getApiUrl(`ticket-settings/update-status/${companyId}`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({
        request_from_ticket: payload.request_from_ticket ?? 'yes',
        ticket_round_robin_status: payload.ticket_round_robin_status,
      }),
    });
  },
  /** POST ticket-settings/agent-settings/{companyId} */
  updateAgentSettings: async (companyId, payload) => {
    const url = getApiUrl(`ticket-settings/agent-settings/${companyId}`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

/** نمایندگان تیکت — ticket-agents، agent-groups */
export const ticketAgentsApi = {
  /** GET — گروه‌ها و کارمندان برای فرم ایجاد نماینده */
  getCreateForm: async () => {
    const url = getApiUrl('ticket-agents/create');
    return apiRequest(url, { method: 'GET' });
  },
  /** GET — گروه‌های قابل انتخاب برای کارمند انتخاب‌شده (بدون گروه‌هایی که قبلاً نماینده داشته‌اند) */
  getAgentGroups: async (agentId) => {
    const url = getApiUrlWithParams('agent-groups', { agent_id: agentId });
    return apiRequest(url, { method: 'GET' });
  },
  create: async ({ user_id, group_id }) => {
    const url = getApiUrl('ticket-agents');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({
        user_id: Number(user_id),
        group_id: Array.isArray(group_id) ? group_id.map((id) => Number(id)) : [],
      }),
    });
  },
  updateStatus: async (userId, { status }) => {
    const url = getApiUrl(`ticket-agents/update-status/${userId}`);
    return apiRequest(url, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },
  updateGroups: async (userId, { group_id }) => {
    const url = getApiUrl(`ticket-agents/update-group/${userId}`);
    return apiRequest(url, {
      method: 'PATCH',
      body: JSON.stringify({
        group_id: Array.isArray(group_id) ? group_id.map((id) => Number(id)) : [],
      }),
    });
  },
  remove: async (userId) => {
    const url = getApiUrl(`ticket-agents/${userId}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** گروه‌های تیکت — ticket-groups */
export const ticketGroupsApi = {
  getCreateForm: async () => {
    const url = getApiUrl('ticket-groups/create');
    return apiRequest(url, { method: 'GET' });
  },
  create: async ({ group_name }) => {
    const url = getApiUrl('ticket-groups');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ group_name }),
    });
  },
  update: async (id, { group_name }) => {
    const url = getApiUrl(`ticket-groups/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify({ group_name }),
    });
  },
  remove: async (id) => {
    const url = getApiUrl(`ticket-groups/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** انواع تیکت — ticket-types */
export const ticketTypesApi = {
  getEdit: async (id) => {
    const url = getApiUrl(`ticket-types/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  create: async ({ type }) => {
    const url = getApiUrl('ticket-types');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ type }),
    });
  },
  update: async (id, { type }) => {
    const url = getApiUrl(`ticket-types/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify({ type }),
    });
  },
  remove: async (id) => {
    const url = getApiUrl(`ticket-types/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** کانال‌های تیکت — ticket-channels */
export const ticketChannelsApi = {
  getEdit: async (id) => {
    const url = getApiUrl(`ticket-channels/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  create: async ({ channel_name }) => {
    const url = getApiUrl('ticket-channels');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ channel_name }),
    });
  },
  update: async (id, { channel_name }) => {
    const url = getApiUrl(`ticket-channels/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify({ channel_name }),
    });
  },
  remove: async (id) => {
    const url = getApiUrl(`ticket-channels/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** قالب پاسخ تیکت — reply-templates */
export const ticketReplyTemplatesApi = {
  getById: async (id) => {
    const url = getApiUrl(`reply-templates/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  getEdit: async (id) => {
    const url = getApiUrl(`reply-templates/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  create: async ({ reply_heading, description }) => {
    const url = getApiUrl('reply-templates');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ reply_heading, description }),
    });
  },
  update: async (id, { reply_heading, reply_text }) => {
    const url = getApiUrl(`reply-templates/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify({ reply_heading, reply_text }),
    });
  },
  remove: async (id) => {
    const url = getApiUrl(`reply-templates/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** اعلان‌های CRM — skipAuthRedirect: خطای 401 نباید کاربر را از پنل خارج کند */
export const notificationsApi = {
  getFollowUpReminders: async () => {
    const url = getApiUrl('notifications/follow-up-reminders');
    return apiRequest(url, { method: 'GET', skipAuthRedirect: true });
  },
  /** پیامک‌های دریافتی جدید خوانده‌نشده برای پاپ‌آپ */
  getUnreadSms: async () => {
    const url = getApiUrl('notifications/unread-sms');
    return apiRequest(url, { method: 'GET', skipAuthRedirect: true });
  },
  markAsRead: async (notificationId) => {
    const url = getApiUrl(`notifications/${notificationId}/read`);
    return apiRequest(url, { method: 'POST', skipAuthRedirect: true });
  },
  markAllAsRead: async () => {
    const url = getApiUrl('notifications/mark-all-read');
    return apiRequest(url, { method: 'POST', skipAuthRedirect: true });
  },
  /** تعویق یادآور — POST /notifications/{id}/snooze */
  snooze: async (notificationId, delayMinutes) => {
    const url = getApiUrl(`notifications/${notificationId}/snooze`);
    return apiRequest(url, {
      method: 'POST',
      skipAuthRedirect: true,
      body: JSON.stringify({ delay_minutes: Number(delayMinutes) }),
    });
  },
};

/** مشتریان — clients */
export const clientsApi = {
  /**
   * GET clients/options — لیست صفحه‌بندی‌شده و قابل جستجوی مشتریان برای فیلترها و فرم‌ها
   * params: { search, page, per_page, include_inactive } یا { ids: [..] } برای برچسب مقادیر انتخاب‌شده
   */
  getOptions: async ({ search, page = 1, per_page = 20, ids, include_inactive } = {}) => {
    const params = ids?.length
      ? { 'ids[]': ids }
      : { search: search || undefined, page, per_page, include_inactive: include_inactive ? 1 : undefined };
    return apiRequest(getApiUrlWithParams('clients/options', params), { method: 'GET' });
  },

  getAll: async (filters = {}) => {
    const params = {
      page: filters.page != null ? filters.page : 1,
      per_page: filters.per_page != null ? filters.per_page : 10,
    };
    if (filters.startDate) params.startDate = String(filters.startDate).replace(/\//g, '-');
    if (filters.endDate) params.endDate = String(filters.endDate).replace(/\//g, '-');
    params.status = filters.status != null && filters.status !== '' ? filters.status : 'all';
    params.client = filters.client != null && filters.client !== '' ? filters.client : 'all';
    params.category_id =
      filters.category_id != null && filters.category_id !== '' ? filters.category_id : 'all';
    params.sub_category_id =
      filters.sub_category_id != null && filters.sub_category_id !== '' ? filters.sub_category_id : 'all';
    params.project_id =
      filters.project_id != null && filters.project_id !== '' ? filters.project_id : 'all';
    params.contract_type_id =
      filters.contract_type_id != null && filters.contract_type_id !== ''
        ? filters.contract_type_id
        : 'all';
    params.verification =
      filters.verification != null && filters.verification !== '' ? filters.verification : 'all';
    if (filters.search) params.search = filters.search;
    const url = getApiUrlWithParams('clients', params);
    return apiRequest(url, { method: 'GET' });
  },
  getCreateForm: async (leadId = null) => {
    const params = leadId != null && leadId !== '' ? { lead: leadId } : {};
    const url = getApiUrlWithParams('clients/create', params);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (formData) => {
    const url = getApiUrl('clients');
    return apiRequest(url, { method: 'POST', body: formData });
  },
  getEditForm: async (id) => {
    const url = getApiUrl(`clients/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  update: async (id, formData) => {
    const url = getApiUrl(`clients/${id}`);
    if (formData instanceof FormData) {
      if (!formData.has('_method')) formData.append('_method', 'PUT');
      return apiRequest(url, { method: 'POST', body: formData });
    }
    return apiRequest(url, { method: 'PUT', body: formData });
  },
  getById: async (id, tab = 'profile') => {
    const params = { tab: tab || 'profile' };
    const url = getApiUrlWithParams(`clients/${id}`, params);
    return apiRequest(url, { method: 'GET' });
  },
  getClientDeals: async (clientId) => {
    const url = getApiUrl(`clients/${clientId}/deals`);
    return apiRequest(url, { method: 'GET' });
  },
  applyQuickAction: async (actionType, rowIds, extra = null) => {
    const url = getApiUrl('clients/apply-quick-action');
    const payload = {
      action_type: actionType,
      row_ids: Array.isArray(rowIds) ? rowIds.join(',') : String(rowIds),
    };
    if (actionType === 'change-status') {
      const status =
        extra != null && typeof extra === 'object' ? extra.status : extra;
      if (status != null && status !== '') {
        payload.status = status;
      }
    }
    if (actionType === 'send-sms') {
      const smsMessage =
        extra != null && typeof extra === 'object' ? extra.message : extra;
      payload.message = smsMessage ?? '';
    }
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

/** دسته‌های مشتری — client-categories */
export const clientCategoriesApi = {
  getCreateData: async () => {
    const url = getApiUrl('client-categories/create');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (categoryName) => {
    const url = getApiUrl('client-categories');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ category_name: categoryName }),
    });
  },
  update: async (id, categoryName) => {
    const url = getApiUrl(`client-categories/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify({ category_name: categoryName }),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`client-categories/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** زیردسته‌های مشتری — client-subcategories */
export const clientSubcategoriesApi = {
  getCreateData: async () => {
    const url = getApiUrl('client-subcategories/create');
    return apiRequest(url, { method: 'GET' });
  },
  getByCategory: async (categoryId) => {
    const url = getApiUrl(`client-subcategories/get-subcategories/${categoryId}`);
    return apiRequest(url, { method: 'GET' });
  },
  create: async ({ category_name, category_id }) => {
    const url = getApiUrl('client-subcategories');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ category_name, category_id }),
    });
  },
  update: async (id, categoryName) => {
    const url = getApiUrl(`client-subcategories/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify({ category_name: categoryName }),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`client-subcategories/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

/** اعلانات — notices */
export const noticesApi = {
  getAll: async (filters = {}) => {
    const params = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      search: filters.search ?? '',
      page: filters.page ?? 1,
      per_page: filters.per_page ?? 10,
    };
    const url = getApiUrlWithParams('notices', params);
    return apiRequest(url, { method: 'GET' });
  },
  getCreateData: async () => {
    const url = getApiUrl('notices/create');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (payload) => {
    const url = getApiUrl('notices');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getById: async (id) => {
    const url = getApiUrl(`notices/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  getEditData: async (id) => {
    const url = getApiUrl(`notices/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  update: async (id, payload) => {
    const url = getApiUrl(`notices/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  delete: async (id) => {
    const url = getApiUrl(`notices/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  applyQuickAction: async (actionType, rowIds) => {
    const url = getApiUrl('notices/apply-quick-action');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({
        action_type: actionType,
        row_ids: Array.isArray(rowIds) ? rowIds.join(',') : String(rowIds),
      }),
    });
  },
  exportFile: async () => {
    const url = getApiUrl('notices/export');
    return downloadExportFile(url, { filenameFallback: 'notices.xlsx' });
  },
};

/** پیام‌ها — messages */
export const messagesApi = {
  getAll: async () => {
    const url = getApiUrl('messages');
    return apiRequest(url, { method: 'GET' });
  },
  getCreateData: async (userType) => {
    const params = {};
    if (userType) params.user_type = userType;
    const url = getApiUrlWithParams('messages/create', params);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (payload) => {
    const url = getApiUrl('messages');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getByUserId: async (userId) => {
    const url = getApiUrl(`messages/${userId}`);
    return apiRequest(url, { method: 'GET' });
  },
  delete: async (messageId) => {
    const url = getApiUrl(`messages/${messageId}`);
    return apiRequest(url, { method: 'DELETE' });
  },
};

// تنظیمات امور مالی — درگاه پرداخت
export const financeSettingsApi = {
  getPaymentGateways: async (tab = 'offline') => {
    const params = { tab };
    const url = getApiUrlWithParams('payment-gateway-settings', params);
    return apiRequest(url, { method: 'GET' });
  },
  getOfflineMethod: async (id) => {
    const url = getApiUrl(`offline-payment-setting/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  createOfflineMethod: async (data) => {
    const url = getApiUrl('offline-payment-setting');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  updateOfflineMethod: async (id, data) => {
    const url = getApiUrl(`offline-payment-setting/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteOfflineMethod: async (id) => {
    const url = getApiUrl(`offline-payment-setting/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  // درگاه‌های پرداخت آنلاین (حساب پذیرنده‌ی خود شرکت)
  getOnlineGateways: async () => {
    const url = getApiUrl('payment-gateways');
    return apiRequest(url, { method: 'GET' });
  },
  createOnlineGateway: async (data) => {
    const url = getApiUrl('payment-gateways');
    return apiRequest(url, { method: 'POST', body: JSON.stringify(data) });
  },
  updateOnlineGateway: async (id, data) => {
    const url = getApiUrl(`payment-gateways/${id}`);
    return apiRequest(url, { method: 'PUT', body: JSON.stringify(data) });
  },
  deleteOnlineGateway: async (id) => {
    const url = getApiUrl(`payment-gateways/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  updatePaymentDomain: async (paymentDomain) => {
    const url = getApiUrl('payment-gateways/domain');
    return apiRequest(url, { method: 'PUT', body: JSON.stringify({ payment_domain: paymentDomain }) });
  },
  verifyPaymentDomain: async () => {
    const url = getApiUrl('payment-gateways/domain/verify');
    return apiRequest(url, { method: 'POST' });
  },
  updatePublicLinkSettings: async (data) => {
    const url = getApiUrl('payment-gateways/link-settings');
    return apiRequest(url, { method: 'PUT', body: JSON.stringify(data) });
  },
  getInvoicePublicLink: async (id) => {
    const url = getApiUrl(`invoices/${id}/public-link`);
    return apiRequest(url, { method: 'GET' });
  },
  // مالیات
  getTaxes: async () => {
    const url = getApiUrl('taxes');
    return apiRequest(url, { method: 'GET' });
  },
  getTax: async (id) => {
    const url = getApiUrl(`taxes/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  createTax: async (data) => {
    const url = getApiUrl('taxes');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  updateTax: async (id, data) => {
    const url = getApiUrl(`taxes/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteTax: async (id) => {
    const url = getApiUrl(`taxes/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  // واحدها
  getUnitType: async (id) => {
    const url = getApiUrl(`unit-type/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  createUnitType: async (data) => {
    const url = getApiUrl('unit-type');
    return apiRequest(url, { method: 'POST', body: JSON.stringify(data) });
  },
  updateUnitType: async (id, data) => {
    const url = getApiUrl(`unit-type/${id}`);
    return apiRequest(url, { method: 'PUT', body: JSON.stringify(data) });
  },
  deleteUnitType: async (id) => {
    const url = getApiUrl(`unit-type/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  setDefaultUnitType: async (unitId) => {
    const url = getApiUrl('unit-type/set-default');
    return apiRequest(url, { method: 'PATCH', body: JSON.stringify({ unit_id: unitId }) });
  },
  // جزئیات پرداخت فاکتور
  getPaymentDetail: async (id) => {
    const url = getApiUrl(`invoices-payment-details/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  createPaymentDetail: async (data) => {
    const url = getApiUrl('invoices-payment-details');
    const formData = new FormData();
    formData.append('title', data.title);
    if (data.payment_details) formData.append('payment_details', data.payment_details);
    if (data.image) formData.append('image', data.image);
    return apiRequest(url, { method: 'POST', body: formData });
  },
  updatePaymentDetail: async (id, data) => {
    const url = getApiUrl(`invoices-payment-details/${id}`);
    const formData = new FormData();
    formData.append('_method', 'PUT');
    formData.append('title', data.title);
    if (data.payment_details) formData.append('payment_details', data.payment_details);
    if (data.image) formData.append('image', data.image);
    if (data.image_delete) formData.append('image_delete', data.image_delete);
    return apiRequest(url, { method: 'POST', body: formData });
  },
  deletePaymentDetail: async (id) => {
    const url = getApiUrl(`invoices-payment-details/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  // پیش‌فاکتورها
  getEstimates: async ({ startDate, endDate, status = 'all', page = 1, per_page = 10 } = {}) => {
    const params = {};
    if (startDate) params.startDate = formatJalaliApiDate(startDate);
    if (endDate) params.endDate = formatJalaliApiDate(endDate);
    if (status) params.status = status;
    params.page = page;
    params.per_page = per_page;
    const url = getApiUrlWithParams('estimates', params);
    return apiRequest(url, { method: 'GET' });
  },
  getEstimateCreateData: async (params = {}) => {
    const query = {};
    if (params.estimate != null && params.estimate !== '') query.estimate = params.estimate;
    if (params.project_id != null && params.project_id !== '') query.project_id = params.project_id;
    if (params.default_client != null && params.default_client !== '') {
      query.default_client = params.default_client;
    }
    const url =
      Object.keys(query).length > 0
        ? getApiUrlWithParams('estimates/create', query)
        : getApiUrl('estimates/create');
    return apiRequest(url, { method: 'GET' });
  },
  createEstimate: async (data) => {
    const url = getApiUrl('estimates');
    const payload = {
      ...data,
      valid_till: formatJalaliApiDate(data?.valid_till),
    };
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getEstimate: async (id) => {
    const url = getApiUrl(`estimates/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  getEstimateEditData: async (id) => {
    const url = getApiUrl(`estimates/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  updateEstimate: async (id, data) => {
    const url = getApiUrl(`estimates/${id}`);
    const payload = {
      ...data,
      valid_till: formatJalaliApiDate(data?.valid_till),
    };
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  sendEstimate: async (id) => {
    const url = getApiUrl(`estimates/send-estimate/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  downloadEstimate: async (id) => {
    const url = getApiUrl(`estimates/download/${id}`);
    return downloadExportFile(url, { filenameFallback: `estimate-${id}.pdf` });
  },
  // فاکتورها
  getInvoices: async (filters = {}) => {
    const params = {};
    if (filters.startDate) params.startDate = formatJalaliApiDate(filters.startDate);
    if (filters.endDate) params.endDate = formatJalaliApiDate(filters.endDate);
    if (filters.status) params.status = filters.status;
    if (filters.client_id) params.client_id = filters.client_id;
    if (filters.project_id) params.project_id = filters.project_id;
    if (filters.page) params.page = filters.page;
    if (filters.per_page) params.per_page = filters.per_page;
    const url = getApiUrlWithParams('invoices', params);
    return apiRequest(url, { method: 'GET' });
  },
  getInvoiceCreateData: async (params = {}) => {
    const query = {};
    if (params.estimate != null && params.estimate !== '') query.estimate = params.estimate;
    if (params.project_id != null && params.project_id !== '') query.project_id = params.project_id;
    if (params.client_id != null && params.client_id !== '') query.client_id = params.client_id;
    const url =
      Object.keys(query).length > 0
        ? getApiUrlWithParams('invoices/create', query)
        : getApiUrl('invoices/create');
    return apiRequest(url, { method: 'GET' });
  },
  createInvoice: async (data) => {
    const url = getApiUrl('invoices');
    const payload = {
      ...data,
      issue_date: formatJalaliApiDate(data?.issue_date),
      due_date: formatJalaliApiDate(data?.due_date),
    };
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getInvoice: async (id) => {
    const url = getApiUrl(`invoices/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  getInvoiceEditData: async (id) => {
    const url = getApiUrl(`invoices/edit/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  updateInvoice: async (id, data) => {
    const url = getApiUrl(`invoices/${id}`);
    const payload = {
      ...data,
      issue_date: formatJalaliApiDate(data?.issue_date),
      due_date: formatJalaliApiDate(data?.due_date),
    };
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  deleteInvoice: async (id) => {
    const url = getApiUrl(`invoices/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },
  sendInvoice: async (id) => {
    const url = getApiUrl(`invoices/send-invoice/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  sendInvoicePaymentReminder: async (id) => {
    const url = getApiUrl(`invoices/payment-reminder/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  downloadInvoice: async (id) => {
    const url = getApiUrl(`invoices/download/${id}`);
    return downloadExportFile(url, { filenameFallback: `invoice-${id}.pdf` });
  },
  getInvoiceCategoryProducts: async (categoryId) => {
    const url = getApiUrl(`invoices/product-category/${categoryId}`);
    return apiRequest(url, { method: 'GET' });
  },
  deleteInvoiceItemImage: async (invoiceItemId) => {
    const url = getApiUrlWithParams('invoices/delete-image', { invoice_item_id: invoiceItemId });
    return apiRequest(url, { method: 'GET' });
  },
  cancelInvoice: async (id) => {
    const url = getApiUrl(`invoices/update-status/${id}`);
    return apiRequest(url, { method: 'GET' });
  },
  changeInvoiceStatus: async ({ invoiceId, status }) => {
    const url = getApiUrl('invoices/change-status');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({
        invoiceId: Number(invoiceId),
        status,
      }),
    });
  },
  updateEstimateStatus: async ({ estimateId, status }) => {
    const url = getApiUrl('estimates/update-status');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({
        estimateId: Number(estimateId),
        status,
      }),
    });
  },
  addInvoiceShippingAddress: async (clientId, shippingAddress) => {
    const url = getApiUrl(`invoices/add-shipping-address/${clientId}`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ shipping_address: shippingAddress }),
    });
  },
  storeInvoiceFile: async (invoiceId, file) => {
    const url = getApiUrl('invoices/store_file');
    const formData = new FormData();
    formData.append('invoice_id', invoiceId);
    if (file) formData.append('file', file);
    return apiRequest(url, { method: 'POST', body: formData });
  },
  // تنظیمات فاکتور
  getInvoiceSettings: async (tab = 'general') => {
    const params = { tab };
    const url = getApiUrlWithParams('invoice-settings', params);
    return apiRequest(url, { method: 'GET' });
  },
  updateGeneralSettings: async (data) => {
    const url = getApiUrl('invoice-settings/update-general');
    return apiRequest(url, { method: 'PUT', body: JSON.stringify(data) });
  },
  updatePrefixSettings: async (data) => {
    const url = getApiUrl('invoice-settings/update-prefix');
    return apiRequest(url, { method: 'PUT', body: JSON.stringify(data) });
  },
  updateTemplateSettings: async (data) => {
    const url = getApiUrl('invoice-settings/update-template');
    return apiRequest(url, { method: 'PUT', body: JSON.stringify(data) });
  },
};

// Petty Cash API
export const pettyCashApi = {
  getAll: async () => {
    const url = getApiUrl('financial/petty-cash');
    return apiRequest(url, { method: 'GET' });
  },
  create: async (payload) => {
    const url = getApiUrl('financial/petty-cash');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  spend: async (id, payload) => {
    const url = getApiUrl(`financial/petty-cash/${id}/spend`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  settle: async (id) => {
    const url = getApiUrl(`financial/petty-cash/${id}/settle`);
    return apiRequest(url, {
      method: 'POST',
    });
  },
};

const api = {
  apiRequest,
  downloadExportFile,
  pettyCashApi,
  departmentsApi,
  designationsApi,
  employeesApi,
  rolesApi,
  companyAddressesApi,
  companySettingsApi,
  businessAddressSettingsApi,
  currencySettingsApi,
  packagesApi,
  companiesApi,
  superadminInvoicesApi,
  faqsApi,
  faqCategoriesApi,
  superadminUsersApi,
  supportTicketsApi,
  supportTicketTypesApi,
  rolePermissionsApi,
  appSettingsApi,
  superadminAppSettingsApi,
  superadminProfileSettingsApi,
  superadminGlobalInvoiceSettingsApi,
  superadminLanguageSettingsApi,
  superadminGlobalCurrencySettingsApi,
  appExceptionsApi,
  myCalendarApi,
  dashboardApi,
  followUpsApi,
  superAdminDashboardApi,
  normalizeSuperAdminDashboardBody,
  profileSettingsApi,
  userProfileApi,
  emergencyContactsApi,
  employeeDocsApi,
  reportsApi,
  hrDashboardApi,
  holidaysApi,
  attendanceApi,
  shiftsApi,
  leavesApi,
  leavesSettingsApi,
  attendanceSettingsApi,
  attendanceRegularizationsApi,
  overtimeRequestsApi,
  attendanceMissionsApi,
  workflowApi,
  employeeShiftsApi,
  shiftRotationsApi,
  leadsApi,
  importApi,
  dealsApi,
  leadSettingsApi,
  leadFormCategoriesApi,
  leadFormsApi,
  smsApi,
  productsApi,
  productCategoriesApi,
  productSubCategoriesApi,
  bankAccountsApi,
  expensesApi,
  expenseCategoriesApi,
  recurringExpensesApi,
  ordersApi,
  ticketsApi,
  callCenterSettingsApi,
  callCenterAgentsApi,
  callCenterCallsApi,
  callCenterPopApi,
  callCenterBlacklistApi,
  callCenterQueuesApi,
  callCenterStandardsApi,
  callCenterOutcomesApi,
  knowledgeCategoriesApi,
  knowledgeBaseApi,
  knowledgeRagApi,
  callCenterKnowledgeCategoriesApi,
  callCenterKnowledgesApi,
  callCenterLiveApi,
  callCenterFakeCallApi,
  callCenterReportsApi,
  ticketSettingsApi,
  ticketAgentsApi,
  ticketGroupsApi,
  ticketTypesApi,
  ticketChannelsApi,
  ticketReplyTemplatesApi,
  clientsApi,
  notificationsApi,
  clientCategoriesApi,
  clientSubcategoriesApi,
  noticesApi,
  messagesApi,
  financeSettingsApi,
  show422Error,
  ValidationError,
  parse422Error,
};

// API های فاز 5 (Targets, Commission Plans, Payroll)
export const targetsApi = {
  getAll: async (filters = {}) => {
    const url = getApiUrlWithParams('financial/targets', filters);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('financial/targets');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

export const commissionPlansApi = {
  getAll: async (filters = {}) => {
    const url = getApiUrlWithParams('financial/commission-plans', filters);
    return apiRequest(url, { method: 'GET' });
  },
  create: async (data) => {
    const url = getApiUrl('financial/commission-plans');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  clawback: async (id, data) => {
    const url = getApiUrl(`financial/commissions/${id}/clawback`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  calculateDeal: async (dealId, planId) => {
    const url = getApiUrlWithParams(`financial/commissions/calculate-deal/${dealId}`, { plan_id: planId });
    return apiRequest(url, { method: 'POST' });
  },
  recordDeal: async (dealId, planId, periodYear, periodMonth) => {
    const url = getApiUrlWithParams(`financial/commissions/record-deal/${dealId}`, { plan_id: planId, period_year: periodYear, period_month: periodMonth });
    return apiRequest(url, { method: 'POST' });
  },
};

export const payrollApi = {
  getAll: async (filters = {}) => {
    const url = getApiUrlWithParams('financial/payroll', filters);
    return apiRequest(url, { method: 'GET' });
  },
  generate: async (data) => {
    const url = getApiUrl('financial/payroll/generate');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  pay: async (id, data) => {
    const url = getApiUrl(`financial/payroll/${id}/pay`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};
export default api;
